import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SegmentedSelect, Field, InfoTip, api, Button, Card, ActiveToggle, ConfirmDialog, PillButton, useBrand, brandDisplayName, apiErrorMessage, chargeClassStatus, brandLocalDate, formatIsoDay, formatMoney } from '@aralere/shared-ui';
import type {
  BrandTaxRate,
  BrandTaxRule,
  Business,
  ChargeClassDraftRef,
  ChargeClassDraftsBulkAction,
  ChargeClassInput,
  ChargeClassPercentBasis,
  ChargeClassRateType,
  ChargeClassRemittanceBasis,
  ChargeClassTaxMode,
  ChargeClassTreatment,
  GlAccountOption,
  GlassIntensity,
  IncomeAccountOption,
  LateFeeConfig,
  EarlyPayDiscountConfig,
  FirstMonthBilling,
  TaxCategory,
  TaxProfileSummary,
  TaxRegistration,
  UnabsorbedCreditDefault,
  UpdateBusinessConfigRequest,
  Vendor,
} from '@aralere/shared-types';
import { RULE_TAX_CATEGORY_OPTIONS, taxCategoryLabel } from './taxCategory';

type Rounding = '0.49' | '0.99' | 'whole';

type SettingsForm = {
  marginEnabled: boolean;
  marginMode: 'margin' | 'markup';
  marginRatePct: string;
  marginRounding: Rounding;
  aiEnabled: boolean;
  aiMode: 'margin' | 'markup';
  aiRatePct: string;
  welcomeEnabled: boolean;
  welcomeHeadline: string;
  welcomeBody: string;
  welcomeVideoUrl: string;
  welcomeStartsAt: string; // YYYY-MM-DD or ''
  bannerEnabled: boolean;
  bannerText: string;
  bannerExpires: string; // YYYY-MM-DD or ''
  glass: GlassIntensity;
  suspendWarnDays: string; // s30 item 5: brand default warning lead days (absent = 15)
  // Time tracking + retainers (Stage-3 slice 3; Q40/Q42/Q44)
  timeIncrement: '15' | '30';
  defaultHourlyRate: string; // '' = no prefill (null)
  notionalRate: string; // '' = lens unavailable for the brand (null)
  retainerAlertsEnabled: boolean;
  retainerLowThresholdPct: string; // 1-99; default 50
  // Mileage (Stage-3 slice 4; Q43). '' = null. federalRate is NATIONAL (Settings writes all brands).
  mileageFederalRate: string;
  mileageBrandRatePerMile: string;
  // Tax + monthly billing (Stage-4 slice 4). The backup jurisdiction is the resolution chain's
  // last step; absent on the doc = us-ca / los-angeles (the locked LA default).
  taxBackupProfileId: string;
  taxBackupJurisdictionId: string;
  /** 04 #160 WS-E fix 1: the brand's "Charge tax by default" switch (`taxEnabledDefault`; absent = false). */
  taxEnabledDefault: boolean;
  composeAutoPull: boolean;
  autoCreditNoteOnRefund: boolean;
  // Convenience fee (s77 #14) - grouped with the tax/billing settings. Applies to NEW invoices only.
  // Form, wire and STORED Business keys all read convenienceFee* (04 #127 clean-slate key rename).
  convenienceFeeEnabled: boolean;
  convenienceFeeRatePct: string;
  // Proration policy (s75; re-cut at 04 #45 O6): the three-way first-month setting (absent = 'immediate';
  // a saved legacy enabled:false reads as 'none') + the live retainers toggle (absent = true).
  prorationFirstMonth: FirstMonthBilling;
  prorationRetainers: boolean;
  // 04 #160 WS-E (02i §8 DP-13): the lapse dialog's brand default. Absent on the doc = 'credit-note'; the
  // form prefills it, so the first save writes it (the noticeHour precedent). Read by the M2 dialog.
  prorationUnabsorbedCredit: UnabsorbedCreditDefault;
  // Non-payment lifecycle (Stage-4 slice 8b). Late fee is active iff value > 0 (the toggle maps to
  // value 0/off). lateFeeValue is the DISPLAY string: percent = "1.5" (rate 0.015), flat = "25.00".
  lateFeeEnabled: boolean;
  lateFeeType: 'flat' | 'percent';
  lateFeeValue: string;
  lateFeeGraceDays: string; // days after due before the fee applies (LateFeeConfig.graceDays)
  suspendGraceDays: string; // days after due before services suspend (§7.10; default 15)
  timezone: string; // IANA; the brand's scheduled-jobs zone (§7.9)
  dailyRunHour: string; // 0-23 local hour the daily late-fee + suspension checks run (default 23)
  noticeHour: string; // 0-23 client-local hour reminders + dunning go out (default 9; 04 #138)
  // Notice/reminder timing (slice 8b) — brand-level. Notices send at the client's local hour.
  reminderAnchor: 'midpoint' | 'days-before-due';
  reminderLeadDaysBeforeDue: string; // used when anchor = days-before-due
  reminderIntervalDays: string; // repeat every N days until paid
  postSuspensionIntervalDays: string; // dunning cadence after suspension (2 = the §7.9 48h)
  // Scheduled-automation switches (slice 8b) — absent on the doc = ON.
  sweepEnabled: boolean;
  lateFeesJobEnabled: boolean;
  remindersJobEnabled: boolean;
  suspensionJobEnabled: boolean;
  // Suspension scope + grace mode (slice 8c) — absent on the doc = 'all' / false / 'fixed' / 30.
  suspensionScope: 'all' | 'invoice-linked';
  suspendOnOneOff: boolean;
  suspendGraceMode: 'fixed' | 'match-terms';
  matchTermsOnReceiptGraceDays: string; // days floor for a due-on-receipt invoice in match-terms mode
  // Charge-through vs credit for suspended time (slice 8c-remainder WS3) — absent on the doc = false
  // (charge-through, the default). On = credit the suspended days on the next monthly invoice.
  creditSuspendedTime: boolean;
  // Voluntary tips (04 #60 WS1) - brand on/off; recording ships with the payment-side build (WS4).
  gratuityEnabled: boolean;
  // Early-payment discount (04 #69; spec 7.24). Offered iff rate > 0: the toggle maps to rate 0 =
  // none, the late-fee value-0 convention. earlyPayRatePct is the DISPLAY string ("2" = rate 0.02).
  earlyPayEnabled: boolean;
  earlyPayRatePct: string;
  earlyPayDays: string; // brand-local calendar days from the invoice date (0-365, last day inclusive)
  earlyPayAllowPartial: boolean;
};

const ROUNDING_OPTIONS: ReadonlyArray<readonly [Rounding, string]> = [
  ['0.99', 'Round up to .99'],
  ['0.49', 'Round up to .49'],
  ['whole', 'Round up to whole dollar'],
];
const GLASS_OPTIONS: ReadonlyArray<readonly [GlassIntensity, string]> = [
  ['default', 'Default'],
  ['frosted', 'Frosted (stronger blur)'],
  ['off', 'Off (flat)'],
];
// 04 #136 THE BRAND CLOCK (02 s177): the brand's time zone may be ANY valid IANA zone (the server
// validates it with Intl, admin-businesses.ts). The US shortlist leads; every zone the browser knows
// follows. A stored value the browser does not list still shows as an extra option at load.
const US_TIMEZONE_OPTIONS: ReadonlyArray<readonly [string, string]> = [
  ['America/Los_Angeles', 'Pacific (America/Los_Angeles)'],
  ['America/Denver', 'Mountain (America/Denver)'],
  ['America/Phoenix', 'Arizona (America/Phoenix)'],
  ['America/Chicago', 'Central (America/Chicago)'],
  ['America/New_York', 'Eastern (America/New_York)'],
  ['America/Anchorage', 'Alaska (America/Anchorage)'],
  ['Pacific/Honolulu', 'Hawaii (Pacific/Honolulu)'],
  ['UTC', 'UTC'],
];
const ALL_TIMEZONES: ReadonlyArray<string> = (() => {
  // Reached through a typed optional so this compiles on any lib level; an older browser lists the shortlist only.
  const intl = Intl as unknown as { supportedValuesOf?: (key: 'timeZone') => string[] };
  try {
    return intl.supportedValuesOf?.('timeZone') ?? [];
  } catch {
    return [];
  }
})();
const TIMEZONE_OPTIONS: ReadonlyArray<readonly [string, string]> = [
  ...US_TIMEZONE_OPTIONS,
  ...ALL_TIMEZONES.filter((z) => !US_TIMEZONE_OPTIONS.some(([v]) => v === z)).map((z) => [z, z] as const),
];
const LATE_FEE_TYPE_OPTIONS: ReadonlyArray<readonly ['flat' | 'percent', string]> = [
  ['percent', 'Percent of the overdue balance'],
  ['flat', 'Flat amount'],
];
const RUN_HOUR_OPTIONS: ReadonlyArray<readonly [string, string]> = Array.from({ length: 24 }, (_, h) => {
  const label = h === 0 ? '12:00 AM' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`;
  return [String(h), label] as const;
});

const pct = (rate: number): string => String(Math.round(rate * 1e6) / 1e4); // 0.3 -> "30"
const toRate = (p: string): number => {
  const n = parseFloat(p);
  return Number.isFinite(n) ? n / 100 : 0;
};
const clampInt = (s: string, min: number, max: number, fallback: number): number => {
  const n = parseInt(s, 10);
  return Number.isFinite(n) && n >= min && n <= max ? n : fallback;
};
const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;
/** Build the LateFeeConfig from the form (slice 8b): value 0 when the toggle is off; percent stores
 *  a rate (1.5 -> 0.015), flat stores dollars. graceDays clamps 0-365. */
const builtLateFee = (f: SettingsForm): LateFeeConfig => {
  const graceDays = clampInt(f.lateFeeGraceDays, 0, 365, 0);
  if (!f.lateFeeEnabled) return { type: f.lateFeeType, value: 0, graceDays };
  const value = f.lateFeeType === 'percent' ? toRate(f.lateFeeValue) : round2(parseFloat(f.lateFeeValue) || 0);
  return { type: f.lateFeeType, value: Math.max(0, value), graceDays };
};
/** Build the EarlyPayDiscountConfig from the form (04 #69): the toggle off = rate 0 = none offered,
 *  with days + allowPartial preserved (the late-fee value-0 convention); percent stores a rate
 *  ("2" -> 0.02). Days clamp 0-365; 0 = only a payment received on the invoice date qualifies. */
const builtEarlyPay = (f: SettingsForm): EarlyPayDiscountConfig => {
  const days = clampInt(f.earlyPayDays, 0, 365, 10);
  const rate = f.earlyPayEnabled ? Math.max(0, toRate(f.earlyPayRatePct)) : 0;
  return { rate, days, allowPartial: f.earlyPayAllowPartial };
};
/** The part of an early-pay config that matters for change detection: "none" whenever the rate is 0,
 *  whatever days/allowPartial it stores, so toggling nothing material never arms the 1.4 opt-in. */
const earlyPayKey = (c: EarlyPayDiscountConfig | null | undefined): string =>
  !c || !(c.rate > 0) ? 'none' : `${c.rate}|${c.days}|${c.allowPartial === true}`;

// ── Sales tax: registrations + the brand overlay (04 #160 WS-E; 02i T3.4 / T5.4 / §8 item 14). Three
//    row lists, so they live in their own state beside SettingsForm (the charge-class precedent). Drafts
//    hold DISPLAY strings (dates as YYYY-MM-DD from <input type="date">, percents as "9.75"); the wire
//    shape is built by builtTaxConfig and validated server-side by api/src/lib/tax-config.ts - the
//    checks here mirror it only so the Save button is honest before the round-trip (never a second
//    authority: the server's refusal is the one that counts, and its message names the row). ──
type RegistrationDraft = {
  profileId: string;
  registrationNumber: string;
  effectiveFrom: string; // YYYY-MM-DD, required
  effectiveTo: string; // YYYY-MM-DD or '' = open-ended
};
type TaxRuleDraft = {
  profileId: string;
  category: TaxCategory;
  taxable: boolean;
  taxableSharePct: string; // '' = the whole line (taxableFraction 1); "80" = 0.8
  reducedRatePct: string; // '' = the jurisdiction's rate; "1" = a reduced rate of 1%
  effectiveFrom: string; // '' = undated
  effectiveTo: string; // '' = open-ended
  note: string;
};
type TaxRateDraft = {
  profileId: string;
  jurisdictionId: string;
  effectiveFrom: string; // YYYY-MM-DD, required
  ratePct: string; // "9.75"
};
type TaxDrafts = { registrations: RegistrationDraft[]; rules: TaxRuleDraft[]; rates: TaxRateDraft[] };

const taxDraftsFromBusiness = (b: Business): TaxDrafts => ({
  registrations: (b.taxRegistrations ?? []).map((r) => ({
    profileId: r.profileId,
    registrationNumber: r.registrationNumber ?? '',
    effectiveFrom: r.effectiveFrom,
    effectiveTo: r.effectiveTo ?? '',
  })),
  rules: (b.taxRules ?? []).map((r) => ({
    profileId: r.profileId,
    category: r.category,
    taxable: r.taxable,
    taxableSharePct: r.taxable && r.taxableFraction !== undefined && r.taxableFraction < 1 ? pct(r.taxableFraction) : '',
    reducedRatePct: r.taxable && r.rate !== undefined ? pct(r.rate) : '',
    effectiveFrom: r.effectiveFrom ?? '',
    effectiveTo: r.effectiveTo ?? '',
    note: r.note ?? '',
  })),
  rates: (b.taxRates ?? []).map((r) => ({
    profileId: r.profileId,
    jurisdictionId: r.jurisdictionId,
    effectiveFrom: r.effectiveFrom,
    ratePct: pct(r.rate),
  })),
});

/** The wire shape (replace semantics: the whole of each list is sent; the server stores an empty list as
 *  null). Blank optional fields are OMITTED, never sent as '' - the server validates what it receives. */
const builtTaxConfig = (d: TaxDrafts): Pick<UpdateBusinessConfigRequest, 'taxRegistrations' | 'taxRules' | 'taxRates'> => ({
  taxRegistrations: d.registrations.map((r): TaxRegistration => ({
    profileId: r.profileId,
    registrationNumber: r.registrationNumber.trim() || null,
    effectiveFrom: r.effectiveFrom,
    effectiveTo: r.effectiveTo || null,
  })),
  taxRules: d.rules.map((r): BrandTaxRule => {
    const share = parseFloat(r.taxableSharePct);
    const reduced = parseFloat(r.reducedRatePct);
    return {
      profileId: r.profileId,
      category: r.category,
      taxable: r.taxable,
      ...(r.taxable && r.taxableSharePct.trim() && Number.isFinite(share) && share < 100 ? { taxableFraction: share / 100 } : {}),
      ...(r.taxable && r.reducedRatePct.trim() && Number.isFinite(reduced) ? { rate: reduced / 100 } : {}),
      ...(r.effectiveFrom ? { effectiveFrom: r.effectiveFrom } : {}),
      ...(r.effectiveTo ? { effectiveTo: r.effectiveTo } : {}),
      ...(r.note.trim() ? { note: r.note.trim() } : {}),
    };
  }),
  taxRates: d.rates.map((r): BrandTaxRate => ({
    profileId: r.profileId,
    jurisdictionId: r.jurisdictionId,
    effectiveFrom: r.effectiveFrom,
    rate: toRate(r.ratePct),
  })),
});

const isYmd = (v: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(v);

/** First blocking problem in the tax drafts, or null (mirrors the server's shape checks; the server's
 *  message is the authority when they disagree). */
const taxDraftError = (d: TaxDrafts, profiles: TaxProfileSummary[]): string | null => {
  const stateName = (id: string): string => profiles.find((p) => p.id === id)?.stateName ?? id;
  // Registrations: a state, a start date, an end date on or after it, no two windows for one state overlapping.
  for (const [i, r] of d.registrations.entries()) {
    const at = `Registration ${i + 1}`;
    if (!r.profileId) return `${at}: pick the state.`;
    if (!isYmd(r.effectiveFrom)) return `${at} (${stateName(r.profileId)}): enter the date the registration took effect.`;
    if (r.effectiveTo && !isYmd(r.effectiveTo)) return `${at} (${stateName(r.profileId)}): the end date is not a date.`;
    if (r.effectiveTo && r.effectiveTo < r.effectiveFrom) return `${at} (${stateName(r.profileId)}): the registration ends before it starts.`;
    if (r.registrationNumber.trim().length > 64) return `${at} (${stateName(r.profileId)}): the permit number is limited to 64 characters.`;
  }
  const byProfile = new Map<string, RegistrationDraft[]>();
  for (const r of d.registrations) byProfile.set(r.profileId, [...(byProfile.get(r.profileId) ?? []), r]);
  for (const [profileId, list] of byProfile) {
    const sorted = [...list].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
    for (let i = 1; i < sorted.length; i += 1) {
      const prev = sorted[i - 1]!;
      if (!prev.effectiveTo || prev.effectiveTo >= sorted[i]!.effectiveFrom) return `Two ${stateName(profileId)} registrations overlap in time. End the earlier one the day before the later one starts, or remove one.`;
    }
  }
  // Rules: a state, a category, a share in (0, 100], a reduced rate in (0, 100), dates in order, one row per (state, category, start).
  const ruleKeys = new Set<string>();
  for (const [i, r] of d.rules.entries()) {
    const at = `Rule ${i + 1}`;
    if (!r.profileId) return `${at}: pick the state.`;
    if (!r.category) return `${at}: pick the category.`;
    if (r.taxable && r.taxableSharePct.trim()) {
      const n = parseFloat(r.taxableSharePct);
      if (!Number.isFinite(n) || n <= 0 || n > 100) return `${at}: the taxable share must be above 0 and at most 100 (blank = the whole line).`;
    }
    if (r.taxable && r.reducedRatePct.trim()) {
      const n = parseFloat(r.reducedRatePct);
      if (!Number.isFinite(n) || n <= 0 || n >= 100) return `${at}: a reduced rate must be above 0 and below 100 (blank = the jurisdiction's rate).`;
    }
    if (r.effectiveFrom && !isYmd(r.effectiveFrom)) return `${at}: the start date is not a date.`;
    if (r.effectiveTo && !isYmd(r.effectiveTo)) return `${at}: the end date is not a date.`;
    if (r.effectiveFrom && r.effectiveTo && r.effectiveFrom > r.effectiveTo) return `${at}: the rule ends before it starts.`;
    if (r.note.trim().length > 200) return `${at}: the note is limited to 200 characters.`;
    const key = `${r.profileId}|${r.category}|${r.effectiveFrom}`;
    if (ruleKeys.has(key)) return `Two ${stateName(r.profileId)} rules for ${taxCategoryLabel(r.category)} start on the same date. Give them different start dates, or remove one.`;
    ruleKeys.add(key);
  }
  // Rates: a state, one of its jurisdictions, a start date, a rate in [0, 100), one row per (state, jurisdiction, start).
  const rateKeys = new Set<string>();
  for (const [i, r] of d.rates.entries()) {
    const at = `Rate ${i + 1}`;
    if (!r.profileId) return `${at}: pick the state.`;
    if (!r.jurisdictionId) return `${at}: pick the jurisdiction.`;
    if (!isYmd(r.effectiveFrom)) return `${at}: enter the date the rate takes effect.`;
    const n = parseFloat(r.ratePct);
    if (!r.ratePct.trim() || !Number.isFinite(n) || n < 0 || n >= 100) return `${at}: the rate must be 0 or more and below 100.`;
    const key = `${r.profileId}|${r.jurisdictionId}|${r.effectiveFrom}`;
    if (rateKeys.has(key)) return `Two ${stateName(r.profileId)} rates for the same jurisdiction start on the same date. Give them different dates, or remove one.`;
    rateKeys.add(key);
  }
  return null;
};

/** A registration's standing on the brand's calendar today. */
const registrationStanding = (r: RegistrationDraft, today: string): { label: string; live: boolean } => {
  if (!isYmd(r.effectiveFrom)) return { label: 'Needs a start date', live: false };
  if (r.effectiveFrom > today) return { label: `Starts ${formatIsoDay(r.effectiveFrom)}`, live: false };
  if (r.effectiveTo && isYmd(r.effectiveTo) && r.effectiveTo < today) return { label: `Ended ${formatIsoDay(r.effectiveTo)}`, live: false };
  return { label: 'In force today', live: true };
};

/** The platform's rows for a (state, category), in words, so a brand rule sits beside what it overrides. */
const platformRuleWords = (profile: TaxProfileSummary | undefined, category: TaxCategory): string => {
  const rows = (profile?.taxability ?? []).filter((r) => r.category === category);
  if (rows.length === 0) return 'Platform: no rule (not taxable).';
  return `Platform: ${rows
    .map((r) => `${r.taxable ? `taxable${r.taxableFraction !== undefined && r.taxableFraction < 1 ? ` on ${pct(r.taxableFraction)}%` : ''}${r.rate !== undefined ? ` at ${pct(r.rate)}%` : ''}` : 'not taxable'}${r.effectiveFrom ? ` from ${formatIsoDay(r.effectiveFrom)}` : ''}${r.effectiveTo ? ` through ${formatIsoDay(r.effectiveTo)}` : ''}`)
    .join('; ')}.`;
};

/** The platform's rate(s) for a jurisdiction, in words. */
const platformRateWords = (profile: TaxProfileSummary | undefined, jurisdictionId: string): string => {
  const j = profile?.jurisdictions.find((x) => x.id === jurisdictionId);
  if (!j) return '';
  const dated = (j.rates ?? []).map((r) => `${pct(r.rate)}% from ${formatIsoDay(r.effectiveFrom)}`);
  return `Platform: ${pct(j.rate)}%${dated.length > 0 ? `; ${dated.join('; ')}` : ''}.`;
};

// ── Charge classes (04 #60 WS1; spec 7.23). The catalog is an array, so it lives in its own state
//    beside SettingsForm. percent drafts hold the DISPLAY percent ("18" = rate 0.18, the pct/toRate
//    convention); flat drafts hold dollars. Ids are server-minted: a draft without one is NEW. ──
type ChargeClassDraft = {
  id?: string;
  name: string;
  rateType: ChargeClassRateType;
  value: string;
  /** Percent classes only (04 #60 WS2a); default 'pre-discount'. */
  percentBasis: ChargeClassPercentBasis;
  taxMode: ChargeClassTaxMode;
  autoApply: boolean;
  /** 04 #125 (WS-A): 'revenue' books to `incomeAccount`; 'pass-through' (collected on behalf of a
   *  payee) books to `liabilityAccount`, names `payeeVendorId` and carries `remittanceBasis`, and is
   *  forced nontaxable. The wire shape sends ONLY the fields the treatment uses (builtChargeClasses).
   *  Immutable once saved: the control is a fixed label on a saved row. */
  treatment: ChargeClassTreatment;
  incomeAccount: string;
  liabilityAccount: string;
  payeeVendorId: string;
  remittanceBasis: ChargeClassRemittanceBasis;
  active: boolean;
  /** 04 #60 WS2b: the brand-local deactivation date, carried through the staged save unchanged
   *  (deactivate / schedule / reactivate are IMMEDIATE actions, never staged). */
  deactivatesAt: string | null;
};

const PERCENT_BASIS_OPTIONS: ReadonlyArray<readonly [ChargeClassPercentBasis, string]> = [
  ['pre-discount', 'Before the invoice discount'],
  ['post-discount', 'After the invoice discount'],
];

const TAX_MODE_OPTIONS: ReadonlyArray<readonly [ChargeClassTaxMode, string]> = [
  ['apportioned', 'Apportioned (follows the taxable lines)'],
  ['taxable', 'Always taxable'],
  ['nontaxable', 'Never taxable'],
];
/** The 13-chart default income account for a new class (Charge-class income). */
const DEFAULT_INCOME_ACCOUNT = '420000';
/** The 13-chart default liability account for a new collected-on-behalf class (Pass-through liabilities). */
const DEFAULT_LIABILITY_ACCOUNT = '240000';
/** 04 #125 rule 6: how much of a collected-on-behalf class is owed to its payee (the lock's two bases). */
const REMITTANCE_BASIS_OPTIONS: ReadonlyArray<readonly [ChargeClassRemittanceBasis, string]> = [
  ['on-collection', 'As collected (what clients have actually paid)'],
  ['on-issue', 'As issued (owed whether or not the client has paid)'],
];
/** The "Books as" labels (02g #125 rule 13; option labels are a label map, never the stored value). */
const TREATMENT_LABEL: Record<ChargeClassTreatment, string> = {
  revenue: 'Revenue',
  'pass-through': 'Collected on behalf',
};

// ── 04 #133: the condensed catalog. Every class is ONE line (name, amount, taxability, account,
//    status, chips) plus its pills; Edit expands the full form in place. The one-line labels are a
//    label map, never the stored value (02d s107 rule). ──
const TAX_MODE_SHORT: Record<ChargeClassTaxMode, string> = {
  apportioned: 'Apportioned',
  taxable: 'Always taxable',
  nontaxable: 'Never taxable',
};
/** "3% before discount" or "$5.00" for the condensed line. */
const chargeAmountSummary = (c: ChargeClassDraft): string =>
  c.rateType === 'percent'
    ? `${c.value.trim() || '0'}% ${c.percentBasis === 'post-discount' ? 'after' : 'before'} discount`
    : formatMoney(parseFloat(c.value) || 0);
/** The DOM id of a class row, the scroll-into-view target when it expands (02d viewing-window rule). */
const classRowDomId = (c: ChargeClassDraft, idx: number): string => `charge-class-${c.id ?? `new-${idx}`}`;

const chargeDraftsFromBusiness = (b: Business): ChargeClassDraft[] =>
  (b.chargeClasses ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    rateType: c.rateType,
    value: c.rateType === 'percent' ? pct(c.value) : String(c.value),
    percentBasis: c.percentBasis ?? 'pre-discount',
    taxMode: c.taxMode,
    autoApply: c.autoApply,
    // 04 #125: absent on a pre-WS-A record = revenue; the unused account field takes its default so a
    // switch on a NEW row (the only row that can switch) starts from the chart default.
    treatment: c.treatment ?? 'revenue',
    incomeAccount: c.incomeAccount ?? DEFAULT_INCOME_ACCOUNT,
    liabilityAccount: c.liabilityAccount ?? DEFAULT_LIABILITY_ACCOUNT,
    payeeVendorId: c.payeeVendorId ?? '',
    remittanceBasis: c.remittanceBasis ?? 'on-collection',
    active: c.active,
    deactivatesAt: c.deactivatesAt ?? null,
  }));

/** First blocking problem in the drafts, or null. Mirrors the server's shape checks. */
const chargeClassDraftError = (list: ChargeClassDraft[]): string | null => {
  const activeNames = new Set<string>();
  for (const d of list) {
    const label = d.name.trim() || 'A new charge class';
    if (!d.name.trim()) return 'Every charge class needs a name.';
    if (d.name.trim().length > 80) return `${label}: the name is limited to 80 characters.`;
    const n = parseFloat(d.value);
    if (!d.value.trim() || !Number.isFinite(n) || n < 0) return `${label} needs a valid amount.`;
    if (d.rateType === 'percent' && n >= 100) return `${label}: the percent must be below 100.`;
    // 04 #125 rule 7: a collected-on-behalf class names who it is collected for (the server refuses
    // the save without it; this keeps the Save button honest before the round-trip).
    if (d.treatment === 'pass-through' && !d.payeeVendorId) return `${label}: pick who this charge is collected for.`;
    if (d.active) {
      const key = d.name.trim().toLowerCase();
      if (activeNames.has(key)) return `Two active charge classes are named '${d.name.trim()}'. Rename or deactivate one.`;
      activeNames.add(key);
    }
  }
  return null;
};

/** The wire shape: percent display -> rate, flat -> 2-decimal dollars; new drafts omit id. 04 #125:
 *  `treatment` is sent EXPLICITLY on every row, and ONLY the account fields that treatment uses go
 *  on the wire (02g #125 rule 2: a revenue row carries incomeAccount and none of the three
 *  pass-through fields; a pass-through row carries the three and no incomeAccount, taxMode forced
 *  'nontaxable'), so the server's typed-field rules are met by construction. */
const builtChargeClasses = (list: ChargeClassDraft[]): ChargeClassInput[] =>
  list.map((d) => ({
    ...(d.id !== undefined ? { id: d.id } : {}),
    name: d.name.trim(),
    rateType: d.rateType,
    value: d.rateType === 'percent' ? toRate(d.value) : round2(parseFloat(d.value) || 0),
    percentBasis: d.percentBasis,
    taxMode: d.treatment === 'pass-through' ? 'nontaxable' : d.taxMode,
    autoApply: d.autoApply,
    treatment: d.treatment,
    ...(d.treatment === 'pass-through'
      ? { liabilityAccount: d.liabilityAccount, payeeVendorId: d.payeeVendorId, remittanceBasis: d.remittanceBasis }
      : { incomeAccount: d.incomeAccount }),
    active: d.active,
    deactivatesAt: d.deactivatesAt,
  }));

/** Rows the drafts list shows before it scrolls (max-h-64 at the list's row height). */
const DRAFT_LIST_ROWS_VISIBLE = 7;

/** 04 #60 WS2b: the bulk choices over the drafts a deactivated class still sits on (02g rule 8:
 *  leave / remove from all / replace in all / charge anyway for all - never convert). */
const BULK_CHOICES: ReadonlyArray<readonly [('leave' | ChargeClassDraftsBulkAction), string, string]> = [
  ['leave', 'Leave the drafts as they are', 'Each draft asks what to do when it is issued.'],
  ['remove', 'Remove the class from every draft', 'The line comes off each draft and its totals recompute.'],
  ['replace', 'Replace it on every draft with another class', 'The other class goes in its place; a draft that already carries it just loses the old line.'],
  ['charge-anyway', 'Charge it anyway on every draft', 'The class is charged as it stands and issue no longer asks about it.'],
];

// Serialize a YYYY-MM-DD banner-expiry date as the END of that day in Pacific time (America/
// Los_Angeles), returned as a UTC ISO string (or null when blank). The <input type="date"> gives a
// calendar day with no time/zone; stamping it as UTC midnight (T00:00:00Z) made "expires the 22nd"
// disappear at 5pm on the 21st in PT. End-of-day Pacific keeps it visible through the whole local
// day. DST-correct (PST/PDT) without a tz library — the read side just compares the resulting instant.
function pacificEndOfDayIso(ymd: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  // 23:59:59 wall-clock on that date; find the UTC instant whose Pacific wall-clock equals it.
  const wallMs = Date.UTC(+m[1], +m[2] - 1, +m[3], 23, 59, 59, 0);
  const offsetMin = laOffsetMinutes(new Date(wallMs)); // e.g. -420 (PDT) / -480 (PST)
  return new Date(wallMs - offsetMin * 60_000).toISOString();
}

// Serialize a YYYY-MM-DD as the START of that day in Pacific time (00:00:00 America/Los_Angeles), as
// a UTC ISO string (or null when blank). Used for welcomeMessage.startsAt — a "show from this day
// onward" date, so it activates at MIDNIGHT Pacific on the chosen day (not end-of-day, which is what
// the banner EXPIRY uses). Round-trips with pacificYmd, so re-saving doesn't creep the date forward.
function pacificStartOfDayIso(ymd: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  const wallMs = Date.UTC(+m[1], +m[2] - 1, +m[3], 0, 0, 0, 0);
  const offsetMin = laOffsetMinutes(new Date(wallMs));
  return new Date(wallMs - offsetMin * 60_000).toISOString();
}

// Pacific (America/Los_Angeles) UTC offset, in minutes, at a given instant — negative west of UTC.
function laOffsetMinutes(at: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);
  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((x) => x.type === type);
    return part ? Number(part.value) : 0;
  };
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return (asUtc - at.getTime()) / 60_000;
}

// Inverse of pacificEndOfDayIso for the <input type="date">: render a stored UTC instant as its
// PACIFIC calendar day (YYYY-MM-DD). An end-of-day-Pacific instant lands on the NEXT UTC day, so
// slicing the ISO string would show tomorrow (and re-saving would creep the date forward each time).
// Empty / invalid → ''.
function pacificYmd(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (t: Intl.DateTimeFormatPartTypes): string => parts.find((x) => x.type === t)?.value ?? '';
  const y = get('year');
  const m = get('month');
  const day = get('day');
  return y && m && day ? `${y}-${m}-${day}` : '';
}

function formFromBusiness(b: Business): SettingsForm {
  return {
    marginEnabled: b.standardMargin.enabled,
    marginMode: b.standardMargin.mode ?? 'margin',
    marginRatePct: pct(b.standardMargin.rate),
    marginRounding: b.standardMargin.rounding,
    aiEnabled: b.aiMargin.enabled,
    aiMode: b.aiMargin.mode ?? 'margin',
    aiRatePct: pct(b.aiMargin.rate),
    welcomeEnabled: b.welcomeMessage.enabled,
    welcomeHeadline: b.welcomeMessage.headline,
    welcomeBody: b.welcomeMessage.body,
    welcomeVideoUrl: b.welcomeMessage.videoUrl ?? '',
    welcomeStartsAt: pacificYmd(b.welcomeMessage.startsAt ?? null),
    bannerEnabled: b.welcomeBanner.enabled,
    bannerText: b.welcomeBanner.text,
    bannerExpires: pacificYmd(b.welcomeBanner.expiresAt),
    glass: b.branding.glassIntensityDefault,
    suspendWarnDays: String(b.suspendWarningLeadDaysDefault ?? 15),
    timeIncrement: b.timeBillingIncrementMinutes === 15 ? '15' : '30',
    defaultHourlyRate: b.defaultHourlyRate != null ? String(b.defaultHourlyRate) : '',
    notionalRate: b.notionalLaborRateDefault != null ? String(b.notionalLaborRateDefault) : '',
    retainerAlertsEnabled: b.retainerAlerts ? b.retainerAlerts.enabled : true,
    retainerLowThresholdPct: String(b.retainerAlerts?.lowThresholdPct ?? 50),
    mileageFederalRate: b.mileage?.federalRate != null ? String(b.mileage.federalRate) : '',
    mileageBrandRatePerMile: b.mileage?.brandRatePerMile != null ? String(b.mileage.brandRatePerMile) : '',
    taxBackupProfileId: b.taxBackupProfile?.profileId ?? 'us-ca',
    taxBackupJurisdictionId: b.taxBackupProfile?.jurisdictionId ?? 'los-angeles',
    taxEnabledDefault: b.taxEnabledDefault === true,
    composeAutoPull: b.composeAutoPullUnbilled ?? false,
    autoCreditNoteOnRefund: b.autoCreditNoteOnRefund ?? false,
    convenienceFeeEnabled: b.convenienceFeeEnabled !== false,
    convenienceFeeRatePct: pct(b.convenienceFeeRate ?? 0.031),
    prorationFirstMonth: b.proration?.firstMonth ?? (b.proration?.enabled === false ? 'none' : 'immediate'),
    prorationRetainers: b.proration?.retainers !== false,
    prorationUnabsorbedCredit: b.proration?.unabsorbedCredit === 'lapse' ? 'lapse' : 'credit-note',
    // Non-payment lifecycle (slice 8b). lateFeeDefault is a required field on the doc; value>0 = on.
    lateFeeEnabled: (b.lateFeeDefault?.value ?? 0) > 0,
    lateFeeType: b.lateFeeDefault?.type === 'flat' ? 'flat' : 'percent',
    lateFeeValue:
      b.lateFeeDefault && b.lateFeeDefault.value > 0
        ? b.lateFeeDefault.type === 'percent'
          ? pct(b.lateFeeDefault.value)
          : String(b.lateFeeDefault.value)
        : '',
    lateFeeGraceDays: String(b.lateFeeDefault?.graceDays ?? 0),
    suspendGraceDays: String(b.suspendGraceDays ?? 15),
    timezone: b.timezone ?? 'America/Los_Angeles',
    dailyRunHour: String(b.dailyRunHour ?? 23),
    noticeHour: String(b.noticeHour ?? 9),
    reminderAnchor: b.reminderAnchor === 'days-before-due' ? 'days-before-due' : 'midpoint',
    reminderLeadDaysBeforeDue: String(b.reminderLeadDaysBeforeDue ?? 7),
    reminderIntervalDays: String(b.reminderIntervalDays ?? 3),
    postSuspensionIntervalDays: String(b.postSuspensionIntervalDays ?? 2),
    sweepEnabled: b.scheduledJobs?.billingSweep !== false,
    lateFeesJobEnabled: b.scheduledJobs?.lateFees !== false,
    remindersJobEnabled: b.scheduledJobs?.reminders !== false,
    suspensionJobEnabled: b.scheduledJobs?.suspension !== false,
    suspensionScope: b.suspensionScope === 'invoice-linked' ? 'invoice-linked' : 'all',
    suspendOnOneOff: b.suspendOnOneOff === true,
    suspendGraceMode: b.suspendGraceMode === 'match-terms' ? 'match-terms' : 'fixed',
    matchTermsOnReceiptGraceDays: String(b.matchTermsOnReceiptGraceDays ?? 30),
    creditSuspendedTime: b.creditSuspendedTime === true,
    gratuityEnabled: b.gratuityEnabled === true,
    // Early-payment discount (04 #69). Absent/null/rate 0 on the doc = none offered.
    earlyPayEnabled: (b.earlyPayDiscount?.rate ?? 0) > 0,
    earlyPayRatePct: b.earlyPayDiscount && b.earlyPayDiscount.rate > 0 ? pct(b.earlyPayDiscount.rate) : '',
    earlyPayDays: String(b.earlyPayDiscount?.days ?? 10),
    earlyPayAllowPartial: b.earlyPayDiscount?.allowPartial === true,
  };
}

// Mirror of api/src/lib/pricing.ts#computePrice — KEEP IN SYNC. Live preview only; the server is
// authoritative on save. margin: cost / (1 - rate); markup: cost * (1 + rate). Rounding applies in both.
function previewMarginPrice(cost: number, rate: number, rounding: Rounding, mode: 'margin' | 'markup'): number | null {
  if (!Number.isFinite(cost) || cost < 0) return null;
  if (rate < 0) return null; // a negative rate is invalid in both modes
  let base: number;
  if (mode === 'markup') {
    base = cost * (1 + rate);
  } else {
    if (rate >= 1) return null; // a margin ≥ 100% is undefined (infinite price) — flagged inline + rejected on save
    base = cost / (1 - rate);
  }
  if (rounding === 'whole') return Math.ceil(base - 1e-9);
  const cents = rounding === '0.49' ? 0.49 : 0.99;
  const whole = Math.floor(base + 1e-9);
  let price = whole + cents;
  if (price < base - 1e-9) price = whole + 1 + cents;
  return Math.round(price * 100) / 100;
}

// Inline (client-side) validation for a rate field, by mode. Returns a plain-English reason to show
// at the field, or null when valid. Empty is treated as 0% (valid) — the blank case is handled on save.
function rateFieldError(pctStr: string, mode: 'margin' | 'markup'): string | null {
  if (!pctStr.trim()) return null;
  const p = parseFloat(pctStr);
  if (!Number.isFinite(p)) return 'Enter a number.';
  const rate = p / 100;
  if (rate < 0) return 'Enter a rate of 0% or higher.';
  if (mode === 'margin' && rate >= 1) return 'Enter a rate under 100% (100% or more is an infinite price).';
  return null;
}

// AI-margin preview: metered pricing is charged to the cent (no .49/.99 rounding). Mode-aware like
// the standard calculator; mirrors the Stage-7 AI pricing that will consume aiMargin.mode.
function previewAiPrice(cost: number, rate: number, mode: 'margin' | 'markup'): number | null {
  if (!Number.isFinite(cost) || cost < 0 || rate < 0) return null;
  if (mode === 'markup') return Math.round(cost * (1 + rate) * 100) / 100;
  if (rate >= 1) return null;
  return Math.round((cost / (1 - rate)) * 100) / 100;
}


export function AdminSettings(): JSX.Element {
  const { brand } = useBrand();

  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [original, setOriginal] = useState<Business | null>(null);
  const [overrideCount, setOverrideCount] = useState(0);
  const [lateFeeOverrideCount, setLateFeeOverrideCount] = useState(0);
  const [suspendGraceOverrideCount, setSuspendGraceOverrideCount] = useState(0);
  const [earlyPayOverrideCount, setEarlyPayOverrideCount] = useState(0); // 04 #69: the 1.4 confirm preview
  const [taxProfiles, setTaxProfiles] = useState<TaxProfileSummary[]>([]);
  // 04 #160 WS-E: the registrations + the brand overlay, staged beside the form (replace semantics on save).
  const [taxDrafts, setTaxDrafts] = useState<TaxDrafts>({ registrations: [], rules: [], rates: [] });
  const [chargeClasses, setChargeClasses] = useState<ChargeClassDraft[]>([]);
  // ── 04 #60 WS2b: deactivation flow state. The drafts census is best-effort (it never blocks the
  //    page); the three dialogs act IMMEDIATELY through the status / bulk endpoints. ──
  const [draftRefs, setDraftRefs] = useState<ChargeClassDraftRef[]>([]);
  const [deactIdx, setDeactIdx] = useState<number | null>(null);
  const [deactDate, setDeactDate] = useState('');
  const [reactivateIdx, setReactivateIdx] = useState<number | null>(null);
  const [bulkIdx, setBulkIdx] = useState<number | null>(null);
  const [bulkChoice, setBulkChoice] = useState<'leave' | ChargeClassDraftsBulkAction>('leave');
  const [bulkReplacement, setBulkReplacement] = useState('');
  const [classBusy, setClassBusy] = useState(false);
  const [draftListOpen, setDraftListOpen] = useState(false);
  /** s167 owner finding: the page-level result banner sits far below the catalog, so the class
   *  actions report right here, inside the card, where the click happened. */
  const [classResult, setClassResult] = useState<string | null>(null);
  const [classError, setClassError] = useState<string | null>(null);
  const [incomeAccounts, setIncomeAccounts] = useState<IncomeAccountOption[]>([]);
  // 04 #125 (WS-A): the pass-through liability picker options and the GLOBAL vendor list (the payee
  // picker; vendors carry no businessId, so one load serves every brand). Both best-effort.
  const [passThroughAccounts, setPassThroughAccounts] = useState<GlAccountOption[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  /** 04 #125 WS-B: owed-now per pass-through class for the selected brand, from the ONE balances
   *  derivation (02g #125 AMENDMENT (C)); best-effort, null = not loaded (the condensed line then
   *  omits its owed tail rather than printing a figure it does not have). */
  const [owedByClass, setOwedByClass] = useState<Record<string, number> | null>(null);
  const [form, setForm] = useState<SettingsForm | null>(null);
  const [applyToAll, setApplyToAll] = useState(false);
  const [applyLifecycleToAll, setApplyLifecycleToAll] = useState(false);
  const [applyEarlyPayToAll, setApplyEarlyPayToAll] = useState(false); // 04 #69: its own 1.4 opt-in
  const [previewCost, setPreviewCost] = useState('100');
  const [aiPreviewCost, setAiPreviewCost] = useState('0.80');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // 1) Load businesses once; default-select the host's brand.
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { businesses: list } = await api.admin.listBusinesses();
        if (!alive) return;
        setBusinesses(list);
        setSelectedId(list.find((b) => b.id === brand?.id)?.id ?? list[0]?.id ?? '');
      } catch (e) {
        if (alive) {
          setError(apiErrorMessage(e));
          setLoading(false);
        }
      }
    })();
    // 04 #125: the payee picker's vendors (GLOBAL, loaded once, best-effort: an empty list renders
    // the "no vendors yet" notice with the Vendors link, never an error).
    void (async () => {
      try {
        const { vendors: list } = await api.admin.listVendors();
        if (alive) setVendors(list);
      } catch {
        if (alive) setVendors([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [brand?.id]);

  /** 04 #125 WS-B: the brand's owed-now figures per pass-through class (best-effort; null on error). */
  const reloadOwed = async (bizId: string): Promise<void> => {
    try {
      const res = await api.admin.remittanceBalances({ businessId: bizId });
      const map: Record<string, number> = {};
      for (const b of res.brands) for (const p of b.payees) for (const c of p.classes) map[c.chargeClassId] = c.owed;
      setOwedByClass(map);
    } catch {
      setOwedByClass(null);
    }
  };
  /** 04 #60 WS2b: the brand's drafts that carry a charge class (best-effort; empty on error). */
  const reloadDraftRefs = async (bizId: string): Promise<void> => {
    try {
      const res = await api.admin.listChargeClassDrafts(bizId);
      setDraftRefs(res.drafts);
    } catch {
      setDraftRefs([]);
    }
  };
  // 2) (Re)load the selected business's config.
  useEffect(() => {
    if (!selectedId) return;
    let alive = true;
    setLoading(true);
    setResult(null);
    setApplyToAll(false);
    setApplyLifecycleToAll(false);
    void (async () => {
      try {
        const res = await api.admin.getBusiness(selectedId);
        if (!alive) return;
        setOriginal(res.business);
        setOverrideCount(res.priceOverrideCount);
        setLateFeeOverrideCount(res.lateFeeOverrideCount ?? 0);
        setSuspendGraceOverrideCount(res.suspendGraceOverrideCount ?? 0);
        setEarlyPayOverrideCount(res.earlyPayOverrideCount ?? 0);
        setTaxProfiles(res.taxProfiles ?? []);
        setTaxDrafts(taxDraftsFromBusiness(res.business));
        setChargeClasses(chargeDraftsFromBusiness(res.business));
        setIncomeAccounts(res.incomeAccounts ?? []);
        setPassThroughAccounts(res.passThroughAccounts ?? []);
        setForm(formFromBusiness(res.business));
        setError(null);
        setDraftRefs([]);
        void reloadDraftRefs(selectedId);
        setOwedByClass(null);
        void reloadOwed(selectedId);
      } catch (e) {
        if (alive) setError(apiErrorMessage(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [selectedId]);

  const brandTimezone = original?.timezone ?? null;
  const statusOf = (c: ChargeClassDraft) => chargeClassStatus(c, brandTimezone);
  /** 04 #125: the payee's display name for a collected-on-behalf class (the id when the vendor list
   *  has not loaded or the vendor is unknown - never blank, so the condensed line always names someone). */
  const vendorName = (id: string): string => vendors.find((v) => v.id === id)?.displayName ?? id;
  /** The payee options: every ACTIVE vendor, plus the row's CURRENT payee even when inactive, so the
   *  <select> always has a matching option (the 04 #81 blank-option trap) and an unrelated edit to the
   *  class never silently re-points it. */
  const payeeOptionsFor = (c: ChargeClassDraft): ReadonlyArray<readonly [string, string]> =>
    vendors
      .filter((v) => v.active !== false || v.id === c.payeeVendorId)
      .map((v) => [v.id, v.active === false ? `${v.displayName} (deactivated)` : v.displayName] as const);
  const draftsFor = (classId: string | undefined): ChargeClassDraftRef[] =>
    classId ? draftRefs.filter((d) => d.chargeClassIds.includes(classId)) : [];
  /** The immediate class actions wait while the catalog has UNSAVED staged edits: the server would
   *  act on the stored catalog and the reload that follows would drop those edits. */
  const savedRows = (list: ChargeClassDraft[]): ChargeClassDraft[] => list.filter((c) => !!c.id);
  const classesDirty = original
    ? JSON.stringify(builtChargeClasses(chargeClasses)) !== JSON.stringify(builtChargeClasses(chargeDraftsFromBusiness(original)))
    : false;
  /** Only unsaved edits to EXISTING classes lock the immediate actions (s167 owner finding): a
   *  new, not-yet-saved row is kept across the reload instead, so it never has to be saved first. */
  const existingDirty = original
    ? JSON.stringify(builtChargeClasses(savedRows(chargeClasses))) !== JSON.stringify(builtChargeClasses(chargeDraftsFromBusiness(original)))
    : false;
  const classActionHint = existingDirty ? 'Save your charge-class changes first; deactivate, reactivate and the draft actions apply immediately.' : null;
  const classActionsLocked = existingDirty || classBusy;
  // ── 04 #133: which SAVED classes are expanded (by id). A new row has no id and is always
  //    expanded; more than one row may be open at once. Every row condenses after a save. ──
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const isExpanded = (c: ChargeClassDraft): boolean => !c.id || expandedIds.includes(c.id);
  const toggleExpanded = (c: ChargeClassDraft, idx: number): void => {
    const id = c.id;
    if (!id) return;
    const opening = !expandedIds.includes(id);
    setExpandedIds((ids) => (opening ? [...ids, id] : ids.filter((x) => x !== id)));
    // 02d viewing-window rule: the form that opens shows where the click happened.
    if (opening) setTimeout(() => document.getElementById(classRowDomId(c, idx))?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 0);
  };
  /** Does this saved row differ from what the server holds? (drives the Unsaved chip on a condensed line) */
  const originalClassById = new Map<string, ChargeClassDraft>(
    original ? chargeDraftsFromBusiness(original).map((c) => [c.id ?? '', c] as const) : [],
  );
  const classUnsaved = (c: ChargeClassDraft): boolean => {
    if (!c.id) return true;
    const orig = originalClassById.get(c.id);
    return !orig || JSON.stringify(builtChargeClasses([c])) !== JSON.stringify(builtChargeClasses([orig]));
  };
  /** Discard changes on a saved row: put back what the server holds (a new row uses discardUnsavedCharge). */
  const revertCharge = (idx: number): void =>
    setChargeClasses((list) => list.map((d, j) => (j === idx && d.id ? originalClassById.get(d.id) ?? d : d)));
  /** s169 owner finding: Save changes sits next to the class being edited. An expanded saved row with
   *  unsaved edits carries its own Save; the Add-row Save shows only for edits that are not covered
   *  that way (a new row, or a dirty row that was collapsed). */
  const inRowSaveCoversAll = chargeClasses.every((c) => !classUnsaved(c) || (!!c.id && isExpanded(c)));
  /** The catalog a per-row save sends: what the server holds, with only this row replaced. */
  const rowCatalogDrafts = (c: ChargeClassDraft): ChargeClassDraft[] =>
    original ? chargeDraftsFromBusiness(original).map((o) => (o.id === c.id ? c : o)) : [c];
  /** Save ONE saved class's edits (s169 owner finding): every other row's unsaved edits, every new
   *  row and the rest of the page stay staged exactly as they were; the row condenses on success. */
  const saveClassRow = async (idx: number): Promise<void> => {
    const c = chargeClasses[idx];
    if (!c?.id || !original || !selectedId) return;
    const classId = c.id;
    setSaving(true);
    setClassError(null);
    setClassResult(null);
    try {
      const res = await api.admin.updateBusinessConfig(selectedId, {
        ...buildBody(formFromBusiness(original), taxDraftsFromBusiness(original)),
        chargeClasses: builtChargeClasses(rowCatalogDrafts(c)),
        applyMarginToAll: false,
        applyLateFeeGraceToAll: false,
        applyEarlyPayToAll: false,
      });
      setOriginal(res.business);
      setChargeClasses((list) => [
        ...chargeDraftsFromBusiness(res.business).map((fresh) => {
          const local = list.find((d) => d.id === fresh.id);
          return local && fresh.id !== classId && classUnsaved(local) ? local : fresh;
        }),
        ...list.filter((d) => !d.id),
      ]);
      setExpandedIds((ids) => ids.filter((x) => x !== classId));
      setClassResult(`${c.name.trim()} saved.`);
    } catch (e) {
      setClassError(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const applyClassResponse = (b: Business): void => {
    setOriginal(b);
    setChargeClasses((list) => [...chargeDraftsFromBusiness(b), ...list.filter((c) => !c.id)]);
  };
  /** Drop a new row that was never saved (a stray "Add charge class"); saved classes are never removed. */
  const discardUnsavedCharge = (idx: number): void =>
    setChargeClasses((list) => list.filter((c, i) => i !== idx || !!c.id));
  const runBulk = async (classId: string, className: string): Promise<string> => {
    if (bulkChoice === 'leave' || !selectedId) return '';
    const res = await api.admin.bulkChargeClassDrafts({
      businessId: selectedId,
      chargeClassId: classId,
      action: bulkChoice,
      ...(bulkChoice === 'replace' ? { replacementChargeClassId: bulkReplacement } : {}),
    });
    const verb = bulkChoice === 'remove' ? 'removed from' : bulkChoice === 'replace' ? 'replaced on' : 'set to charge anyway on';
    let msg = `${className} ${verb} ${res.updated.length} draft${res.updated.length === 1 ? '' : 's'}.`;
    if (res.failed.length > 0) msg += ` ${res.failed.length} draft${res.failed.length === 1 ? '' : 's'} could not be updated: ${res.failed.map((f) => f.clientName).join(', ')}.`;
    return msg;
  };
  const confirmDeactivate = async (): Promise<void> => {
    if (deactIdx === null || !selectedId) return;
    const c = chargeClasses[deactIdx];
    if (!c?.id) return;
    setClassBusy(true);
    setClassError(null);
    setClassResult(null);
    try {
      const res = await api.admin.setChargeClassStatus(selectedId, { chargeClassId: c.id, deactivatesAt: deactDate });
      applyClassResponse(res.business);
      const today = brandLocalDate(brandTimezone);
      const bits: string[] = [deactDate === today ? `${c.name.trim()} deactivated.` : `${c.name.trim()} deactivates on ${formatIsoDay(deactDate)}.`];
      // A class deactivated today moves under the divider condensed (04 #133); a scheduled one stays as it was.
      if (deactDate === today) setExpandedIds((ids) => ids.filter((x) => x !== c.id));
      if (deactDate === today) {
        const bulkMsg = await runBulk(c.id, c.name.trim());
        if (bulkMsg) bits.push(bulkMsg);
      }
      setClassResult(bits.join(' '));
      setDeactIdx(null);
      void reloadDraftRefs(selectedId);
    } catch (e) {
      // The dialog closes and the reason shows in the card: a refused action must never leave the
      // dialog open with its reason rendered off-screen (s167 owner finding).
      setClassError(apiErrorMessage(e));
      setDeactIdx(null);
      void reloadDraftRefs(selectedId);
    } finally {
      setClassBusy(false);
    }
  };
  const confirmReactivate = async (): Promise<void> => {
    if (reactivateIdx === null || !selectedId) return;
    const c = chargeClasses[reactivateIdx];
    if (!c?.id) return;
    setClassBusy(true);
    setClassError(null);
    setClassResult(null);
    try {
      const res = await api.admin.setChargeClassStatus(selectedId, { chargeClassId: c.id, deactivatesAt: null });
      applyClassResponse(res.business);
      setClassResult(`${c.name.trim()} is active again${c.autoApply ? ' and will be added to every new invoice' : ''}.`);
      setReactivateIdx(null);
      void reloadDraftRefs(selectedId);
    } catch (e) {
      setClassError(apiErrorMessage(e));
      setReactivateIdx(null);
    } finally {
      setClassBusy(false);
    }
  };
  const confirmBulk = async (): Promise<void> => {
    if (bulkIdx === null || !selectedId) return;
    const c = chargeClasses[bulkIdx];
    if (!c?.id) return;
    setClassBusy(true);
    setClassError(null);
    setClassResult(null);
    try {
      const msg = await runBulk(c.id, c.name.trim());
      setClassResult(msg || 'No change made.');
      setBulkIdx(null);
      void reloadDraftRefs(selectedId);
    } catch (e) {
      setClassError(apiErrorMessage(e));
      setBulkIdx(null);
      void reloadDraftRefs(selectedId);
    } finally {
      setClassBusy(false);
    }
  };
  const openDeactivate = (idx: number): void => {
    if (classActionsLocked) return;
    setDeactDate(brandLocalDate(brandTimezone));
    setBulkChoice('leave');
    setBulkReplacement('');
    setDraftListOpen(false);
    setDeactIdx(idx);
  };
  const openBulk = (idx: number): void => {
    if (classActionsLocked) return;
    setBulkChoice('leave');
    setBulkReplacement('');
    setDraftListOpen(false);
    setBulkIdx(idx);
  };
  const openReactivate = (idx: number): void => {
    if (classActionsLocked) return;
    setReactivateIdx(idx);
  };
  const bulkChoiceReady = bulkChoice !== 'replace' || !!bulkReplacement;
  /** "N drafts reference this class" + a toggle that expands the list in place (client, date,
   *  subtotal, total): a brand can have hundreds of drafts, so names never sit inline. */
  const renderDraftCount = (refs: ChargeClassDraftRef[], phrase: string): JSX.Element => (
    <div className="mt-3">
      <p>
        {refs.length === 0 ? `No draft invoice ${phrase}.` : (
          <>
            <strong>{refs.length}</strong> draft invoice{refs.length === 1 ? '' : 's'} {phrase}.{' '}
            <button type="button" className="underline decoration-dotted underline-offset-2 hover:decoration-solid" onClick={() => setDraftListOpen((o) => !o)}>
              {draftListOpen ? 'Hide drafts' : 'View drafts'}
            </button>
          </>
        )}
      </p>
      {draftListOpen && refs.length > 0 ? (
        <div className="mt-2">
          {refs.length > DRAFT_LIST_ROWS_VISIBLE ? (
            <p className="mb-1 text-xs text-[var(--color-muted)]">All {refs.length} drafts are listed; scroll inside the list to see the rest.</p>
          ) : null}
        <div className="max-h-64 overflow-y-auto rounded-md border border-[rgb(var(--edge-rgb)/0.10)]">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[var(--surface-solid)] text-left text-[var(--color-muted)]">
              <tr>
                <th className="px-2 py-1 font-normal">Client</th>
                <th className="px-2 py-1 font-normal">Draft date</th>
                <th className="px-2 py-1 text-right font-normal">Subtotal</th>
                <th className="px-2 py-1 text-right font-normal">Total (no card fee)</th>
              </tr>
            </thead>
            <tbody>
              {refs.map((d) => (
                <tr key={d.invoiceId} className="border-t border-[rgb(var(--edge-rgb)/0.05)]">
                  <td className="px-2 py-1">{d.clientName}</td>
                  <td className="px-2 py-1">{formatIsoDay(d.issueDate)}</td>
                  <td className="px-2 py-1 text-right">{formatMoney(d.subtotal)}</td>
                  <td className="px-2 py-1 text-right">{formatMoney(d.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      ) : null}
    </div>
  );
  /** The bulk choice list, shared by the deactivate dialog (immediate case) and the drafts dialog. */
  const renderBulkChoice = (classId: string): JSX.Element => {
    const others = chargeClasses.filter((o) => o.id && o.id !== classId && statusOf(o) !== 'inactive');
    return (
      <div className="mt-3 space-y-2">
        {BULK_CHOICES.map(([value, label, hint]) => (
          <label key={value} className="flex items-start gap-2 text-sm">
            <input type="radio" name="charge-class-bulk" className="mt-1" checked={bulkChoice === value} onChange={() => setBulkChoice(value)} />
            <span>
              {label}
              <span className="block text-xs text-[var(--color-muted)]">{hint}</span>
            </span>
          </label>
        ))}
        {bulkChoice === 'replace' ? (
          others.length > 0 ? (
            <Select
              value={bulkReplacement}
              onChange={setBulkReplacement}
              options={[['', 'Choose the replacement class...'] as const, ...others.map((o) => [o.id ?? '', o.name.trim()] as const)]}
            />
          ) : (
            <p className="text-xs text-[var(--tint-red-text)]">No other active class exists yet; create one first.</p>
          )
        ) : null}
        <p className="text-xs text-[var(--color-muted)]">Converting a charge to a plain line is a per-draft decision, made in the invoice editor.</p>
      </div>
    );
  };

  const update = (patch: Partial<SettingsForm>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const updateCharge = (idx: number, patch: Partial<ChargeClassDraft>) =>
    setChargeClasses((list) => list.map((d, j) => (j === idx ? { ...d, ...patch } : d)));

  /** 04 #133: one class row, used by BOTH the active list and the deactivated list. The condensed
   *  line always shows (name, chips, then amount / taxability / account while collapsed, then the
   *  status and drafts lines) with the row's pills beside it; Edit expands the full form in place.
   *  Below sm the line and the pills stack, and everything wraps, so nothing scrolls sideways. */
  const renderClassRow = (c: ChargeClassDraft, idx: number): JSX.Element => {
    const status = statusOf(c);
    const expanded = isExpanded(c);
    const refs = status === 'inactive' ? draftsFor(c.id) : [];
    const acknowledged = refs.filter((d) => c.id && d.acknowledgedChargeClassIds.includes(c.id)).length;
    const chip = 'rounded-full px-2 py-0.5 text-[11px] leading-4';
    /** The in-row Save validates only what it sends: this row against the saved catalog. */
    const rowErr = c.id ? chargeClassDraftError(rowCatalogDrafts(c)) : null;
    return (
      <div key={c.id ?? `new-${idx}`} id={classRowDomId(c, idx)} className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] p-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 text-sm">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-medium">{c.name.trim() || (c.id ? 'Unnamed class' : 'New charge class')}</span>
              {c.autoApply ? <span className={`${chip} bg-[var(--tint-blue-bg)] text-[var(--tint-blue-text)]`}>Auto</span> : null}
              {c.id && classUnsaved(c) ? <span className={`${chip} bg-[var(--tint-warn-bg)] text-[var(--tint-warn-text)]`}>Unsaved</span> : null}
            </div>
            {!expanded ? (
              <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-[var(--color-muted)]">
                <span>{chargeAmountSummary(c)}</span>
                <span aria-hidden="true">&middot;</span>
                <span>{c.treatment === 'pass-through' ? TAX_MODE_SHORT.nontaxable : TAX_MODE_SHORT[c.taxMode]}</span>
                <span aria-hidden="true">&middot;</span>
                <span>{c.treatment === 'pass-through' ? `Collected for ${c.payeeVendorId ? vendorName(c.payeeVendorId) : 'a payee not yet chosen'}` : `Books to ${c.incomeAccount}`}</span>
                {/* 04 #125 WS-B (02g AMENDMENT (C)): the owed tail reads the ONE balances derivation; negative is
                    stated as over-paid (rule 4), never hidden. Absent while the figure has not loaded. */}
                {c.treatment === 'pass-through' && c.id && owedByClass && owedByClass[c.id] !== undefined ? (
                  <>
                    <span aria-hidden="true">&middot;</span>
                    <span className={owedByClass[c.id] < 0 ? 'text-[var(--tint-red-text)]' : ''}>
                      {owedByClass[c.id] < 0 ? `over-paid by ${formatMoney(-owedByClass[c.id])}` : `${formatMoney(owedByClass[c.id])} owed`}
                    </span>
                    <span aria-hidden="true">&middot;</span>
                    <Link className="underline decoration-dotted underline-offset-2 hover:decoration-solid" to={`/admin/remittances?business=${encodeURIComponent(selectedId)}`}>
                      View remittances &rarr;
                    </Link>
                  </>
                ) : null}
              </div>
            ) : null}
            {status === 'scheduled' && c.deactivatesAt ? (
              <span className="block text-xs text-[var(--tint-warn-text)]">Deactivates on {formatIsoDay(c.deactivatesAt)}</span>
            ) : null}
            {status === 'inactive' && c.deactivatesAt ? (
              <span className="block text-xs text-[var(--color-muted)]">Deactivated on {formatIsoDay(c.deactivatesAt)}</span>
            ) : null}
            {refs.length > 0 ? (
              <span className="block text-xs text-[var(--tint-warn-text)]">
                {refs.length} draft{refs.length === 1 ? '' : 's'} still reference{refs.length === 1 ? 's' : ''} this class
                {acknowledged > 0 ? ` (${acknowledged} already set to charge anyway)` : ''}.
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end">
            {status === 'scheduled' && c.id ? (
              <PillButton tone="green" onClick={() => openReactivate(idx)}>
                Keep active
              </PillButton>
            ) : null}
            {status !== 'inactive' && c.id ? (
              <PillButton tone="amber" onClick={() => openDeactivate(idx)}>
                {status === 'scheduled' ? 'Change date' : 'Deactivate'}
              </PillButton>
            ) : null}
            {status === 'inactive' && refs.length > 0 && c.id ? (
              <PillButton tone="amber" onClick={() => openBulk(idx)}>
                Resolve drafts
              </PillButton>
            ) : null}
            {status === 'inactive' && c.id ? (
              <PillButton tone="green" onClick={() => openReactivate(idx)}>
                Reactivate
              </PillButton>
            ) : null}
            {c.id ? (
              <PillButton onClick={() => toggleExpanded(c, idx)}>{expanded ? 'Close' : 'Edit'}</PillButton>
            ) : (
              <PillButton tone="red" onClick={() => discardUnsavedCharge(idx)}>
                Discard
              </PillButton>
            )}
          </div>
        </div>
        {expanded ? (
          <div className="mt-3">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Name" tip="The line description clients see on the invoice, e.g. 'Service Charge'. Renaming later never changes already-issued invoices.">
                <TextInput value={c.name} onChange={(v) => updateCharge(idx, { name: v })} placeholder="Service Charge" />
              </Field>
              <Field label="Amount">
                <div className="flex flex-wrap items-center gap-2">
                  <SegmentedSelect
                    layout="joined"
                    ariaLabel="Charge amount type"
                    options={[
                      { value: 'percent', label: 'Percent' },
                      { value: 'flat', label: 'Flat' },
                    ]}
                    value={c.rateType}
                    onChange={(v) => updateCharge(idx, { rateType: v })}
                  />
                  <div className="w-28">
                    <NumberInput value={c.value} onChange={(v) => updateCharge(idx, { value: v })} suffix={c.rateType === 'percent' ? '%' : 'USD'} />
                  </div>
                </div>
              </Field>
              {c.rateType === 'percent' ? (
                <Field
                  label="Percent applies to"
                  tip="Before (the default): the percent is figured on the ordinary lines before any invoice-level discount, so a discount on the work never shrinks this charge. After: figured on what the ordinary lines come to after the invoice discount. The discount itself is never taken off the charge either way."
                >
                  <Select value={c.percentBasis} onChange={(v) => updateCharge(idx, { percentBasis: v as ChargeClassPercentBasis })} options={PERCENT_BASIS_OPTIONS} />
                </Field>
              ) : null}
              <Field
                label="Books as"
                tip="Revenue: this charge is your own income and books to an income account. Collected on behalf: you collect it for someone else (a government body, a third party, tips owed to staff) and owe it to them; it books to a liability, never to revenue, and is never taxed. Set when the class is created and locked after that: to change it, deactivate this class and create a new one."
              >
                {c.id ? (
                  <p className="px-1 py-2 text-sm">
                    {TREATMENT_LABEL[c.treatment]}
                    <span className="block text-xs text-[var(--color-muted)]">Locked since this class was created. Deactivate it and create a new class to change how it books.</span>
                  </p>
                ) : (
                  <SegmentedSelect
                    layout="joined"
                    ariaLabel="Books as"
                    options={[
                      { value: 'revenue', label: TREATMENT_LABEL.revenue },
                      { value: 'pass-through', label: TREATMENT_LABEL['pass-through'] },
                    ]}
                    value={c.treatment}
                    // A switch resets taxability to the mode the treatment allows: forced nontaxable
                    // on collected-on-behalf (02g #125 rule 3), back to the apportioned default on revenue.
                    onChange={(v) => updateCharge(idx, { treatment: v, taxMode: v === 'pass-through' ? 'nontaxable' : 'apportioned' })}
                  />
                )}
              </Field>
              {/* Layout (owner ruling s191): the right column always holds "Books to". A FLAT class has no
                  "Percent applies to", so an empty cell after "Books as" keeps the pairs aligned:
                  Collected for | Taxability, then Owed to the payee | Books to (revenue: Taxability | Books to). */}
              {c.rateType === 'flat' ? <div className="hidden md:block" aria-hidden="true" /> : null}
              {c.treatment === 'pass-through' ? (
                <Field
                  label="Collected for"
                  required
                  tip="The vendor this money belongs to: the agency, the third party or the staff member it is paid out to. The amount owed to them accrues as invoices carrying this charge are issued or paid (per the basis below) and is cleared by recording a remittance. One class is owed to one payee."
                >
                  {vendors.length === 0 ? (
                    <p className="rounded-md border border-[var(--tint-warn-border)] bg-[var(--tint-warn-bg-soft)] px-3 py-2 text-sm text-[var(--tint-warn-text)]">
                      There are no vendors yet. A collected-on-behalf charge is owed to a vendor record, so create one first to get started.{' '}
                      <Link to="/admin/vendors" className="underline">Go to Vendors</Link>
                    </p>
                  ) : (
                    <Select
                      value={c.payeeVendorId}
                      onChange={(v) => updateCharge(idx, { payeeVendorId: v })}
                      options={[['', 'Choose a vendor'] as const, ...payeeOptionsFor(c)]}
                    />
                  )}
                </Field>
              ) : null}
              <Field
                label="Taxability"
                tip={
                  c.treatment === 'pass-through'
                    ? 'Money collected on behalf of someone else is not your sale, so it is outside your gross receipts and never taxed. If the amount IS your sale, book the class as revenue instead.'
                    : "Apportioned (the default) taxes the charge in proportion to the invoice's taxable lines, so it stays untaxed on an all-services invoice. Set at issue per invoice; issued invoices never change."
                }
              >
                {c.treatment === 'pass-through' ? (
                  <p className="px-1 py-2 text-sm">
                    {TAX_MODE_SHORT.nontaxable}
                    <span className="block text-xs text-[var(--color-muted)]">Fixed for a collected-on-behalf charge.</span>
                  </p>
                ) : (
                  <Select value={c.taxMode} onChange={(v) => updateCharge(idx, { taxMode: v as ChargeClassTaxMode })} options={TAX_MODE_OPTIONS} />
                )}
              </Field>
              {c.treatment === 'pass-through' ? (
                <Field
                  label="Owed to the payee"
                  tip="As collected (the default): you owe the payee the share of each invoice the client has actually paid, so you never remit money you have not received. As issued: you owe the full charge the moment the invoice is issued, whether or not the client pays. A tip recorded with a payment is owed in full either way."
                >
                  <Select value={c.remittanceBasis} onChange={(v) => updateCharge(idx, { remittanceBasis: v as ChargeClassRemittanceBasis })} options={REMITTANCE_BASIS_OPTIONS} />
                </Field>
              ) : null}
              {c.treatment === 'pass-through' ? (
                <Field label="Books to" tip="The liability account the collected amount sits in until it is paid out. 240000 Pass-through liabilities for an amount collected for a third party; 241000 Tips payable for tips owed to staff. From the locked chart of accounts.">
                  {passThroughAccounts.length > 0 ? (
                    <Select
                      value={c.liabilityAccount}
                      onChange={(v) => updateCharge(idx, { liabilityAccount: v })}
                      options={passThroughAccounts.map((a) => [a.number, `${a.number} ${a.name}`] as const)}
                    />
                  ) : (
                    <p className="px-1 py-2 text-sm text-[var(--color-muted)]">{c.liabilityAccount}</p>
                  )}
                </Field>
              ) : (
                <Field label="Books to" tip="The income account this class's revenue lands in for reporting. From the locked chart of accounts; 420000 Charge-class income is the default.">
                  {incomeAccounts.length > 0 ? (
                    <Select
                      value={c.incomeAccount}
                      onChange={(v) => updateCharge(idx, { incomeAccount: v })}
                      options={incomeAccounts.map((a) => [a.number, `${a.number} ${a.name}`] as const)}
                    />
                  ) : (
                    <p className="px-1 py-2 text-sm text-[var(--color-muted)]">{c.incomeAccount}</p>
                  )}
                </Field>
              )}
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={c.autoApply} onChange={(e) => updateCharge(idx, { autoApply: e.target.checked })} />
              <span>
                Add to every new invoice automatically
                <span className="block text-xs text-[var(--color-muted)]">Either way it can be added or removed per invoice before issue.</span>
              </span>
            </label>
            {c.id && classUnsaved(c) ? (
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <Button onClick={() => void saveClassRow(idx)} loading={saving} disabled={saving || !!rowErr}>
                  Save changes
                </Button>
                <Button variant="tertiary" onClick={() => revertCharge(idx)} disabled={saving}>
                  Discard changes
                </Button>
                {rowErr ? <span className="text-xs text-[var(--tint-red-text)]">{rowErr}</span> : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };
  const addChargeClass = () =>
    setChargeClasses((list) => [
      ...list,
      // 02g defaults: taxMode 'apportioned', the 420000 income account; opt into autoApply per class.
      { name: '', rateType: 'percent', value: '', percentBasis: 'pre-discount', taxMode: 'apportioned', autoApply: false, treatment: 'revenue', incomeAccount: DEFAULT_INCOME_ACCOUNT, liabilityAccount: DEFAULT_LIABILITY_ACCOUNT, payeeVendorId: '', remittanceBasis: 'on-collection', active: true, deactivatesAt: null },
    ]);

  const marginChanged = !!(
    original &&
    form &&
    (form.marginEnabled !== original.standardMargin.enabled ||
      form.marginMode !== (original.standardMargin.mode ?? 'margin') ||
      toRate(form.marginRatePct) !== original.standardMargin.rate ||
      form.marginRounding !== original.standardMargin.rounding)
  );

  // Non-payment lifecycle change detection (slice 8b) — gates the §1.4 opt-in visibility + confirm.
  const lateFeeChanged = !!(
    original && form && JSON.stringify(builtLateFee(form)) !== JSON.stringify(original.lateFeeDefault)
  );
  const graceChanged = !!(
    original && form && clampInt(form.suspendGraceDays, 0, 365, 15) !== (original.suspendGraceDays ?? 15)
  );
  // The §1.4 opt-in only matters when a CHANGED default has overrides to clear.
  const lifecycleOptInApplies =
    (lateFeeChanged && lateFeeOverrideCount > 0) || (graceChanged && suspendGraceOverrideCount > 0);
  // 04 #69: the early-pay default has its own 1.4 opt-in (a separate server flag). Compared on the
  // material part only: an "off" form against a null or rate-0 doc is not a change.
  const earlyPayChanged = !!(original && form && earlyPayKey(builtEarlyPay(form)) !== earlyPayKey(original.earlyPayDiscount));
  const earlyPayOptInApplies = earlyPayChanged && earlyPayOverrideCount > 0;

  /** The request body for the form given (default: the live form and the live tax drafts). saveClassRow
   *  builds one from the ORIGINAL form and ORIGINAL tax drafts so a per-row save sends nothing else that
   *  is still staged on the page. */
  const buildBody = (f: SettingsForm = form as SettingsForm, t: TaxDrafts = taxDrafts): UpdateBusinessConfigRequest => {
    return {
      standardMargin: { enabled: f.marginEnabled, mode: f.marginMode, rate: toRate(f.marginRatePct), rounding: f.marginRounding },
      aiMargin: { enabled: f.aiEnabled, mode: f.aiMode, rate: toRate(f.aiRatePct) },
      welcomeMessage: {
        enabled: f.welcomeEnabled,
        headline: f.welcomeHeadline,
        body: f.welcomeBody,
        videoUrl: f.welcomeVideoUrl.trim() || null,
        // Start-of-day PACIFIC: a "show from" date activates at midnight PT on the chosen day; null =
        // no schedule (active immediately when enabled). Round-trips via pacificYmd (no day creep).
        startsAt: pacificStartOfDayIso(f.welcomeStartsAt),
      },
      welcomeBanner: {
        enabled: f.bannerEnabled,
        text: f.bannerText,
        // End-of-day PACIFIC, not UTC midnight: "expires the 22nd" must show through the whole
        // local day (a UTC-midnight stamp hid it on the evening of the 21st PT). See pacificEndOfDayIso.
        expiresAt: pacificEndOfDayIso(f.bannerExpires),
      },
      glassIntensityDefault: f.glass,
      applyMarginToAll: marginChanged && applyToAll,
      // s30 item 5: the brand default lead days for the pre-auto-cancel data-loss warning. Clamp to
      // a sane integer client-side; the server validates 1-365.
      suspendWarningLeadDaysDefault: (() => {
        const n = parseInt(f.suspendWarnDays, 10);
        return Number.isFinite(n) && n >= 1 && n <= 365 ? n : 15;
      })(),
      // Time tracking + retainers (Q40/Q42/Q44). Blank money fields send null (clears the value);
      // the server validates amounts. The threshold clamps client-side; server validates 1-99.
      timeBillingIncrementMinutes: f.timeIncrement === '15' ? 15 : 30,
      defaultHourlyRate: (() => {
        const n = parseFloat(f.defaultHourlyRate);
        return f.defaultHourlyRate.trim() && Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
      })(),
      notionalLaborRateDefault: (() => {
        const n = parseFloat(f.notionalRate);
        return f.notionalRate.trim() && Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
      })(),
      retainerAlerts: {
        enabled: f.retainerAlertsEnabled,
        lowThresholdPct: (() => {
          const n = parseInt(f.retainerLowThresholdPct, 10);
          return Number.isFinite(n) && n >= 1 && n <= 99 ? n : 50;
        })(),
      },
      // Mileage (Q43). Blank sends null (clears). Per-mile rates keep up to 3 decimals (tenths of a
      // cent). The federal rate is national — the server propagates it to every brand.
      mileageFederalRate: (() => {
        const n = parseFloat(f.mileageFederalRate);
        return f.mileageFederalRate.trim() && Number.isFinite(n) && n >= 0 ? Math.round(n * 1000) / 1000 : null;
      })(),
      mileageBrandRatePerMile: (() => {
        const n = parseFloat(f.mileageBrandRatePerMile);
        return f.mileageBrandRatePerMile.trim() && Number.isFinite(n) && n >= 0 ? Math.round(n * 1000) / 1000 : null;
      })(),
      // Tax + monthly billing (slice 4). Only sent when the picker has profiles to choose from;
      // degraded (no profiles shipped) leaves the stored value untouched.
      ...(taxProfiles.length > 0
        ? { taxBackupProfile: { profileId: f.taxBackupProfileId, jurisdictionId: f.taxBackupJurisdictionId } }
        : {}),
      // 04 #160 WS-E fix 1: the brand's "Charge tax by default" switch (the invoice-level gate inside a
      // registered state); the server writes it only when it changed (the configHistory diff).
      taxEnabledDefault: f.taxEnabledDefault,
      // 04 #160 WS-E: where the brand collects + its own rules and rates - the WHOLE of each list every
      // save (replace semantics; the server stores an empty list as null and writes nothing when an absent
      // list stays empty). Degraded (no profiles shipped) sends none of the three and leaves the stored
      // values untouched, exactly like the backup picker. taxProvider is read-only here and never sent.
      ...(taxProfiles.length > 0 ? builtTaxConfig(t) : {}),
      composeAutoPullUnbilled: f.composeAutoPull,
      autoCreditNoteOnRefund: f.autoCreditNoteOnRefund,
      convenienceFeeEnabled: f.convenienceFeeEnabled,
      convenienceFeeRate: toRate(f.convenienceFeeRatePct),
      proration: { firstMonth: f.prorationFirstMonth, retainers: f.prorationRetainers, unabsorbedCredit: f.prorationUnabsorbedCredit },
      // Non-payment lifecycle (slice 8b). Late fee off => value 0 (type + graceDays preserved).
      lateFeeDefault: builtLateFee(f),
      suspendGraceDays: clampInt(f.suspendGraceDays, 0, 365, 15),
      timezone: f.timezone,
      dailyRunHour: clampInt(f.dailyRunHour, 0, 23, 23),
      noticeHour: clampInt(f.noticeHour, 0, 23, 9),
      reminderAnchor: f.reminderAnchor,
      reminderLeadDaysBeforeDue: clampInt(f.reminderLeadDaysBeforeDue, 0, 365, 7),
      reminderIntervalDays: clampInt(f.reminderIntervalDays, 1, 365, 3),
      postSuspensionIntervalDays: clampInt(f.postSuspensionIntervalDays, 1, 365, 2),
      suspensionScope: f.suspensionScope,
      suspendOnOneOff: f.suspendOnOneOff,
      suspendGraceMode: f.suspendGraceMode,
      matchTermsOnReceiptGraceDays: clampInt(f.matchTermsOnReceiptGraceDays, 0, 365, 30),
      creditSuspendedTime: f.creditSuspendedTime,
      scheduledJobs: {
        billingSweep: f.sweepEnabled,
        lateFees: f.lateFeesJobEnabled,
        reminders: f.remindersJobEnabled,
        suspension: f.suspensionJobEnabled,
      },
      // Charge classes + voluntary tips (04 #60 WS1). The WHOLE catalog is sent every save (replace
      // semantics; the server enforces never-delete and mints ids for new drafts).
      chargeClasses: builtChargeClasses(chargeClasses),
      gratuityEnabled: f.gratuityEnabled,
      // 04 #69: the brand's early-pay default. Off with nothing stored = null (no write); off with a
      // stored config = rate 0 (days + allowPartial kept, the late-fee value-0 convention).
      earlyPayDiscount: f.earlyPayEnabled || original?.earlyPayDiscount ? builtEarlyPay(f) : null,
      // §1.4 opt-in: only meaningful when a changed default has overrides (server re-checks what changed).
      applyLateFeeGraceToAll: applyLifecycleToAll && lifecycleOptInApplies,
      applyEarlyPayToAll: applyEarlyPayToAll && earlyPayOptInApplies,
    };
  };

  const doSave = async (): Promise<void> => {
    if (!form || !selectedId) return;
    setSaving(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.admin.updateBusinessConfig(selectedId, buildBody());
      setOriginal(res.business);
      setForm(formFromBusiness(res.business));
      setTaxDrafts(taxDraftsFromBusiness(res.business));
      setChargeClasses(chargeDraftsFromBusiness(res.business));
      setExpandedIds([]); // 04 #133: every class condenses once its edits are saved
      void reloadOwed(selectedId); // 04 #125 WS-B: a new or re-pointed pass-through class gets its owed tail
      setApplyToAll(false);
      setApplyLifecycleToAll(false);
      setApplyEarlyPayToAll(false);
      const fresh = await api.admin.getBusiness(selectedId);
      setOverrideCount(fresh.priceOverrideCount);
      setLateFeeOverrideCount(fresh.lateFeeOverrideCount ?? 0);
      setSuspendGraceOverrideCount(fresh.suspendGraceOverrideCount ?? 0);
      setEarlyPayOverrideCount(fresh.earlyPayOverrideCount ?? 0);
      const bits: string[] = ['Settings saved.'];
      if (res.marginServicesRepriced > 0) bits.push(`${res.marginServicesRepriced} service price(s) updated.`);
      if (res.overridesCleared > 0) bits.push(`${res.overridesCleared} per-client override(s) cleared.`);
      const lifecycleCleared = (res.lateFeeOverridesCleared ?? 0) + (res.suspendGraceOverridesCleared ?? 0);
      if (lifecycleCleared > 0) bits.push(`${lifecycleCleared} per-client late-fee/grace override(s) reverted to the new default.`);
      if ((res.earlyPayOverridesCleared ?? 0) > 0) bits.push(`${res.earlyPayOverridesCleared} per-client early-pay override(s) reverted to the new default.`);
      setResult(bits.join(' '));
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  const onSave = (): void => {
    // §1.4: gate behind a confirm only when applying a changed default to clients that actually hold
    // overrides — margin (price) and/or the late-fee / grace defaults (slice 8b).
    const marginConfirm = marginChanged && applyToAll && overrideCount > 0;
    const lifecycleConfirm = applyLifecycleToAll && lifecycleOptInApplies;
    const earlyPayConfirm = applyEarlyPayToAll && earlyPayOptInApplies;
    if (marginConfirm || lifecycleConfirm || earlyPayConfirm) setConfirmOpen(true);
    else void doSave();
  };

  if (loading && !form) return <p className="text-sm text-[var(--color-muted)]">Loading…</p>;
  if (error && !form) return <p className="text-sm text-[var(--tint-red-text)]">Error: {error}</p>;
  if (!form) return <p className="text-sm text-[var(--color-muted)]">No business selected.</p>;

  const previewPrice = previewMarginPrice(parseFloat(previewCost), toRate(form.marginRatePct), form.marginRounding, form.marginMode);
  const markupMode = form.marginMode === 'markup';
  const stdRateErr = form.marginEnabled ? rateFieldError(form.marginRatePct, form.marginMode) : null;
  const aiMarkup = form.aiMode === 'markup';
  const aiRateErr = form.aiEnabled ? rateFieldError(form.aiRatePct, form.aiMode) : null;
  const chargeErr = chargeClassDraftError(chargeClasses);
  const taxErr = taxProfiles.length > 0 ? taxDraftError(taxDrafts, taxProfiles) : null; // 04 #160 WS-E
  const aiPreviewPrice = previewAiPrice(parseFloat(aiPreviewCost), toRate(form.aiRatePct), form.aiMode);
  const brandToday = brandLocalDate(brandTimezone);
  const profileOf = (id: string): TaxProfileSummary | undefined => taxProfiles.find((p) => p.id === id);
  const updateRegistration = (idx: number, patch: Partial<RegistrationDraft>): void =>
    setTaxDrafts((d) => ({ ...d, registrations: d.registrations.map((r, j) => (j === idx ? { ...r, ...patch } : r)) }));
  const updateTaxRule = (idx: number, patch: Partial<TaxRuleDraft>): void =>
    setTaxDrafts((d) => ({ ...d, rules: d.rules.map((r, j) => (j === idx ? { ...r, ...patch } : r)) }));
  const updateTaxRate = (idx: number, patch: Partial<TaxRateDraft>): void =>
    setTaxDrafts((d) => ({ ...d, rates: d.rates.map((r, j) => (j === idx ? { ...r, ...patch } : r)) }));
  const removeRegistration = (idx: number): void => setTaxDrafts((d) => ({ ...d, registrations: d.registrations.filter((_, j) => j !== idx) }));
  const removeTaxRule = (idx: number): void => setTaxDrafts((d) => ({ ...d, rules: d.rules.filter((_, j) => j !== idx) }));
  const removeTaxRate = (idx: number): void => setTaxDrafts((d) => ({ ...d, rates: d.rates.filter((_, j) => j !== idx) }));
  const firstProfileId = taxProfiles[0]?.id ?? '';
  const addRegistration = (): void =>
    setTaxDrafts((d) => ({ ...d, registrations: [...d.registrations, { profileId: firstProfileId, registrationNumber: '', effectiveFrom: brandToday, effectiveTo: '' }] }));
  const addTaxRule = (): void =>
    setTaxDrafts((d) => ({ ...d, rules: [...d.rules, { profileId: firstProfileId, category: 'software.prewritten', taxable: true, taxableSharePct: '', reducedRatePct: '', effectiveFrom: '', effectiveTo: '', note: '' }] }));
  const addTaxRate = (): void =>
    setTaxDrafts((d) => ({ ...d, rates: [...d.rates, { profileId: firstProfileId, jurisdictionId: taxProfiles[0]?.jurisdictions[0]?.id ?? '', effectiveFrom: brandToday, ratePct: '' }] }));
  const stateOptions: ReadonlyArray<readonly [string, string]> = taxProfiles.map((tp) => [tp.id, tp.stateName] as const);
  const dateInputClass = 'w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]';

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Settings</h1>
          <p className="text-sm text-[var(--color-muted)]">
            Pricing, welcome content, and appearance for a brand. Changes take effect on the client's next refresh.
          </p>
        </div>
        {businesses.length > 1 ? (
          <label className="block">
            <span className="block text-sm font-medium mb-1.5">Brand</span>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
            >
              {businesses.map((b) => (
                <option key={b.id} value={b.id}>
                  {brandDisplayName(b.name)}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </header>

      {/* Standard margin */}
      <Card>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl">Standard pricing</h2>
            <p className="text-sm text-[var(--color-muted)]">
              Auto-prices fixed-cost services and margin-priced packages from your cost, rounded up. Pick either
              margin or markup below. Services with a hand-set price are unaffected; turning this off
              freezes auto-priced services at their current price.
            </p>
          </div>
          <ActiveToggle
            active={form.marginEnabled}
            busy={false}
            label="standard margin"
            onToggle={() => update({ marginEnabled: !form.marginEnabled })}
          />
        </div>

        {/* Pricing mode — margin XOR markup (slice 5.3) */}
        <div className={['mt-4', form.marginEnabled ? '' : 'opacity-50 pointer-events-none'].join(' ')}>
          <span className="block text-sm font-medium mb-1.5">Pricing mode</span>
          <SegmentedSelect
            layout="joined"
            ariaLabel="Pricing mode"
            options={[
              { value: 'margin', label: 'Margin' },
              { value: 'markup', label: 'Markup' },
            ]}
            value={form.marginMode}
            onChange={(v) => update({ marginMode: v })}
          />
          <p className="mt-2 text-sm text-[var(--color-muted)]">
            {markupMode
              ? 'Markup: client price = cost × (1 + rate). Any rate works (a 100% markup = 2× cost).'
              : 'Margin (true gross margin): client price = cost ÷ (1 − rate). The rate must be under 100% (100% or more is an infinite price).'}
          </p>
        </div>

        <div
          className={['grid gap-4 md:grid-cols-2 mt-4', form.marginEnabled ? '' : 'opacity-50 pointer-events-none'].join(
            ' ',
          )}
        >
          <Field label={markupMode ? 'Markup (%)' : 'Margin (%)'}>
            <NumberInput value={form.marginRatePct} onChange={(v) => update({ marginRatePct: v })} suffix="%" invalid={!!stdRateErr} />
            {stdRateErr ? <p className="mt-1 text-xs text-[var(--tint-red-text)]">{stdRateErr}</p> : null}
          </Field>
          <Field label="Rounding">
            <Select
              value={form.marginRounding}
              onChange={(v) => update({ marginRounding: v as Rounding })}
              options={ROUNDING_OPTIONS}
            />
          </Field>
        </div>

        {form.marginEnabled ? (
          <div className="mt-3 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] px-4 py-3 text-sm flex flex-wrap items-center gap-2">
            <span className="text-[var(--color-muted)]">Preview:</span>
            <span>cost $</span>
            <input
              value={previewCost}
              onChange={(e) => setPreviewCost(e.target.value)}
              type="number"
              step="any"
              className="w-24 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-2 py-1 text-sm bg-[var(--surface-solid)]"
            />
            <span>→ {markupMode ? 'markup' : 'margin'} price</span>
            {stdRateErr ? (
              <span className="font-medium text-[var(--tint-red-text)]">{stdRateErr}</span>
            ) : (
              <>
                <span className="font-medium">{previewPrice != null ? `$${previewPrice.toFixed(2)}` : '-'}</span>
                {previewPrice != null && Number.isFinite(parseFloat(previewCost)) ? (
                  <span className="text-xs text-[var(--color-muted)]">
                    {markupMode
                      ? `= $${(parseFloat(previewCost) || 0).toFixed(2)} × (1 + ${Math.round(toRate(form.marginRatePct) * 100)}%)`
                      : `= $${(parseFloat(previewCost) || 0).toFixed(2)} ÷ (1 − ${Math.round(toRate(form.marginRatePct) * 100)}%)`}
                  </span>
                ) : null}
              </>
            )}
          </div>
        ) : null}

        {/* §1.4 opt-in */}
        {marginChanged ? (
          overrideCount > 0 ? (
            <label className="mt-4 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={applyToAll}
                onChange={(e) => setApplyToAll(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Apply to all clients, including those with manual price overrides.
                <span className="block text-xs text-[var(--color-muted)]">
                  {overrideCount} client{overrideCount === 1 ? ' has a' : 's have'} manual price override
                  {overrideCount === 1 ? '' : 's'}. Off (default) preserves them; on reverts them to the new margin price.
                </span>
              </span>
            </label>
          ) : (
            <p className="mt-4 flex items-center gap-1.5 text-sm text-[var(--color-muted)]">
              No per-client price overrides to update on this brand.
              <InfoTip text="None of this brand's auto-priced services currently have a client on a custom price, so this pricing change has nothing to revert. The 'apply to all clients' option only appears when one does. A custom price on a manually-priced service is never affected by a pricing change." />
            </p>
          )
        ) : null}
      </Card>

      {/* AI margin */}
      <Card>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl">AI margin</h2>
            <p className="text-sm text-[var(--color-muted)]">
              Prices metered AI usage from its token cost (separate from standard pricing). Consumed by the
              AI Support Center when metering.
            </p>
          </div>
          <ActiveToggle
            active={form.aiEnabled}
            busy={false}
            label="AI margin"
            onToggle={() => update({ aiEnabled: !form.aiEnabled })}
          />
        </div>

        {/* AI pricing mode — margin XOR markup (slice 5.3) */}
        <div className={['mt-4', form.aiEnabled ? '' : 'opacity-50 pointer-events-none'].join(' ')}>
          <span className="block text-sm font-medium mb-1.5">Pricing mode</span>
          <SegmentedSelect
            layout="joined"
            ariaLabel="AI pricing mode"
            options={[
              { value: 'margin', label: 'Margin' },
              { value: 'markup', label: 'Markup' },
            ]}
            value={form.aiMode}
            onChange={(v) => update({ aiMode: v })}
          />
          <p className="mt-2 text-sm text-[var(--color-muted)]">
            {aiMarkup
              ? 'Markup: client price = cost × (1 + rate). Any rate works (a 100% markup = 2× cost).'
              : 'Margin (true gross margin): client price = cost ÷ (1 − rate). The rate must be under 100% (100% or more is an infinite price).'}
          </p>

          <div className="mt-4 md:w-1/2">
            <Field label={aiMarkup ? 'Markup (%)' : 'Margin (%)'}>
              <NumberInput value={form.aiRatePct} onChange={(v) => update({ aiRatePct: v })} suffix="%" invalid={!!aiRateErr} />
              {aiRateErr ? <p className="mt-1 text-xs text-[var(--tint-red-text)]">{aiRateErr}</p> : null}
            </Field>
          </div>

          <div className="mt-3 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] px-4 py-3 text-sm flex flex-wrap items-center gap-2">
            <span className="text-[var(--color-muted)]">Preview:</span>
            <span>token cost $</span>
            <input
              value={aiPreviewCost}
              onChange={(e) => setAiPreviewCost(e.target.value)}
              type="number"
              step="any"
              className="w-24 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-2 py-1 text-sm bg-[var(--surface-solid)]"
            />
            <span>→ {aiMarkup ? 'markup' : 'margin'} price</span>
            {aiRateErr ? (
              <span className="font-medium text-[var(--tint-red-text)]">{aiRateErr}</span>
            ) : (
              <>
                <span className="font-medium">{aiPreviewPrice != null ? `$${aiPreviewPrice.toFixed(2)}` : '-'}</span>
                {aiPreviewPrice != null && Number.isFinite(parseFloat(aiPreviewCost)) ? (
                  <span className="text-xs text-[var(--color-muted)]">
                    {aiMarkup
                      ? `= $${(parseFloat(aiPreviewCost) || 0).toFixed(2)} × (1 + ${Math.round(toRate(form.aiRatePct) * 100)}%)`
                      : `= $${(parseFloat(aiPreviewCost) || 0).toFixed(2)} ÷ (1 − ${Math.round(toRate(form.aiRatePct) * 100)}%)`}
                  </span>
                ) : null}
              </>
            )}
            <span className="w-full text-xs text-[var(--color-muted)]">Metered to the cent (no .49/.99 rounding). AI pricing goes live with the AI Support Center.</span>
          </div>
        </div>
      </Card>

      {/* Welcome message */}
      <Card>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl">Welcome message</h2>
            <p className="text-sm text-[var(--color-muted)]">Shown once to a new client on first sign-in.</p>
          </div>
          <ActiveToggle
            active={form.welcomeEnabled}
            busy={false}
            label="welcome message"
            onToggle={() => update({ welcomeEnabled: !form.welcomeEnabled })}
          />
        </div>
        <div className={['space-y-4 mt-4', form.welcomeEnabled ? '' : 'opacity-50 pointer-events-none'].join(' ')}>
          <Field label="Headline">
            <TextInput
              value={form.welcomeHeadline}
              onChange={(v) => update({ welcomeHeadline: v })}
              placeholder="Welcome to your portal"
            />
          </Field>
          <Field label="Body">
            <textarea
              value={form.welcomeBody}
              onChange={(e) => update({ welcomeBody: e.target.value })}
              rows={3}
              className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
            />
          </Field>
          <Field label="Video URL (optional)">
            <TextInput
              value={form.welcomeVideoUrl}
              onChange={(v) => update({ welcomeVideoUrl: v })}
              placeholder="https://www.youtube.com/embed/…"
            />
            <p className="mt-1 text-xs text-[var(--color-muted)]">
              A hosted video link (a YouTube/Vimeo embed URL or a direct video file), shown inside the
              welcome modal. Leave blank for no video.
            </p>
          </Field>
          <Field label="Start date (optional)">
            <input
              type="date"
              value={form.welcomeStartsAt}
              onChange={(e) => update({ welcomeStartsAt: e.target.value })}
              className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
            />
            <p className="mt-1 text-xs text-[var(--color-muted)]">
              Schedules the welcome: it stays hidden until this day (Pacific), then shows to everyone who
              hasn’t seen this version. Blank shows it immediately when enabled. Editing any welcome
              content re-shows it for everyone.
            </p>
          </Field>
        </div>
      </Card>

      {/* §12: the per-brand welcomeBanner authoring card was retired here — broadcasts are now
          composed on the admin Notifications page (receipt-backed, per-user lifecycle). The
          welcomeBanner business field is dormant (kept only as a legacy note in 01); buildBody still
          round-trips its stored value so saving Settings doesn't wipe it. */}

      {/* Service lifecycle (s30 item 5 / 08 Q30) */}
      <Card>
        <div>
          <h2 className="font-display text-xl">Service lifecycle</h2>
          <p className="text-sm text-[var(--color-muted)]">
            Defaults for the per-service lifecycle policy. Enforcement runs in the billing phase; the values are
            stored now.
          </p>
        </div>
        <div className="mt-4 md:w-1/2">
          <Field
            label="Data-loss warning lead (before auto-cancel)"
            tip="Days before a service's AUTO-CANCEL date that the client is warned their data may be lost. This is the service-cancellation warning. It is NOT the non-payment suspension (that is 'Suspend for non-payment after', under Late fees, early-pay discounts and non-payment). A service can override this; blank service values inherit this brand default."
          >
            <NumberInput value={form.suspendWarnDays} onChange={(v) => update({ suspendWarnDays: v })} suffix="days" />
          </Field>
        </div>
      </Card>

      {/* Time tracking + retainers (Stage-3 slice 3; Q40/Q42/Q44) */}
      <Card>
        <div>
          <h2 className="font-display text-xl">Time and retainers</h2>
          <p className="text-sm text-[var(--color-muted)]">
            Defaults for logged time under this brand. The increment and notional rate are applied when an
            entry is saved; changing them later never rewrites existing entries. Per-client overrides live on
            the client editor.
          </p>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <Field
            label="Billing increment"
            tip="Logged time rounds UP to this before charging (22 minutes at a 15-minute increment bills as 30). A client can override it."
          >
            <Select
              value={form.timeIncrement}
              onChange={(v) => update({ timeIncrement: v as '15' | '30' })}
              options={[
                ['30', '30 minutes (default)'],
                ['15', '15 minutes'],
              ]}
            />
          </Field>
          <Field
            label="Default hourly rate"
            tip="Pre-fills the rate on new time entries (always editable per entry; a client override wins). Blank means no prefill."
          >
            <NumberInput value={form.defaultHourlyRate} onChange={(v) => update({ defaultHourlyRate: v })} suffix="$/hr" />
          </Field>
          <Field
            label="Notional labor rate"
            tip="The admin-only 'my time is worth $X/hour' lens for profitability reports. Never shown to clients and never on an invoice. Blank turns the lens off for this brand."
          >
            <NumberInput value={form.notionalRate} onChange={(v) => update({ notionalRate: v })} suffix="$/hr" />
          </Field>
        </div>
        <div className="mt-6 flex items-start justify-between gap-4">
          <div>
            <h3 className="font-medium">Retainer alerts</h3>
            <p className="text-sm text-[var(--color-muted)]">
              In-app notices to the client's billing contacts and to you when a retainer's included hours run
              low or run out for the month. The depletion notice names the overage rate.
            </p>
          </div>
          <ActiveToggle
            active={form.retainerAlertsEnabled}
            busy={false}
            label="retainer alerts"
            onToggle={() => update({ retainerAlertsEnabled: !form.retainerAlertsEnabled })}
          />
        </div>
        {form.retainerAlertsEnabled ? (
          <div className="mt-3 md:w-1/2">
            <Field
              label="Low-hours threshold"
              tip="The 'running low' notice fires when the percent of included hours REMAINING this month drops to this value or below. The default is 50."
            >
              <NumberInput
                value={form.retainerLowThresholdPct}
                onChange={(v) => update({ retainerLowThresholdPct: v })}
                suffix="% remaining"
              />
            </Field>
          </div>
        ) : null}
      </Card>

      {/* Mileage (Stage-3 slice 4; Q43) */}
      <Card>
        <div>
          <h2 className="font-display text-xl">Mileage</h2>
          <p className="text-sm text-[var(--color-muted)]">
            The IRS rate is your deduction basis and the fallback billed rate. The billed rate per mile
            resolves in order: a client override, then this brand's rate, then a margin or markup over
            the IRS rate (using your standard pricing mode, when it's on), then the IRS rate. Rates are
            snapshotted onto each entry when it is saved.
          </p>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <Field
            label="IRS federal rate"
            tip="The maintained IRS standard mileage rate, updated each January. This is NATIONAL: saving it here sets it for every brand. It is your deduction basis (cost) and the billed-rate fallback."
          >
            <NumberInput value={form.mileageFederalRate} onChange={(v) => update({ mileageFederalRate: v })} suffix="$/mi" />
          </Field>
          <Field
            label="This brand's rate"
            tip="A per-mile rate for this brand that wins over the over-IRS derivation and the IRS fallback. A per-client override still wins over this. Blank means fall back to the over-IRS derivation or the IRS rate."
          >
            <NumberInput value={form.mileageBrandRatePerMile} onChange={(v) => update({ mileageBrandRatePerMile: v })} suffix="$/mi" />
          </Field>
        </div>
      </Card>

      {/* Tax + monthly billing (Stage-4 slice 4) */}
      <Card>
        <div>
          <h2 className="font-display text-xl">Tax and monthly billing</h2>
          <p className="text-sm text-[var(--color-muted)]">
            An invoice's tax rate resolves in order: a per-invoice override, the client's override, the
            client's address jurisdiction, then this backup jurisdiction. The composer builds a client's
            Monthly draft from their active services and packages.
          </p>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="space-y-4">
          {/* 04 #160 WS-E fix 1: the brand's "Charge tax by default" switch. Until it had an editor the
              seed value (off) was the only writer, so every invoice had to opt in by hand. */}
          <Field
            label="Charge tax by default"
            tip="The Charge tax box on a new invoice starts from this. On: every invoice collects tax where the brand is registered, each line by its state's rule; a client's own switch (coming with the client tax panel) and a per-invoice override still win. Off: each invoice opts in by hand, which is how nothing was collected before the registrations editor existed."
          >
            <SegmentedSelect
              layout="joined"
              ariaLabel="Charge tax by default"
              options={[
                { value: 'on', label: 'On: collect where registered' },
                { value: 'off', label: 'Off: each invoice opts in' },
              ]}
              value={form.taxEnabledDefault ? 'on' : 'off'}
              onChange={(v) => update({ taxEnabledDefault: v === 'on' })}
            />
            <p className="mt-1 text-xs text-[var(--color-muted)]">
              {form.taxEnabledDefault
                ? 'New invoices start with Charge tax on and the rate from the client\'s address; a registration below still decides whether anything is collected.'
                : 'New invoices start with Charge tax off: no tax is collected unless you tick the box on each invoice. Turn this on before go-live.'}
            </p>
          </Field>
          <Field
            label="Backup tax jurisdiction"
            tip="Used only when a client has no override and no usable address. The default is Los Angeles, California (9.75%)."
          >
            {taxProfiles.length > 0 ? (
              <div className="flex gap-2">
                <Select
                  value={form.taxBackupProfileId}
                  onChange={(v) =>
                    update({
                      taxBackupProfileId: v,
                      taxBackupJurisdictionId:
                        taxProfiles.find((tp) => tp.id === v)?.jurisdictions[0]?.id ?? '',
                    })
                  }
                  options={taxProfiles.map((tp) => [tp.id, tp.stateName] as const)}
                />
                <Select
                  value={form.taxBackupJurisdictionId}
                  onChange={(v) => update({ taxBackupJurisdictionId: v })}
                  options={(taxProfiles.find((tp) => tp.id === form.taxBackupProfileId)?.jurisdictions ?? []).map(
                    (j) => [j.id, `${j.name} (${String(Number((j.rate * 100).toPrecision(12)))}%)`] as const,
                  )}
                />
              </div>
            ) : (
              <p className="text-sm text-[var(--color-muted)]">
                No tax profiles are loaded on the server, so the brand default rate applies. Check the deploy
                if this persists.
              </p>
            )}
          </Field>
          <div className="border-t border-[rgb(var(--edge-rgb)/0.10)] pt-4 space-y-4">
            <div>
              <h3 className="font-medium">Proration</h3>
              <p className="text-sm text-[var(--color-muted)]">
                Monthly invoices bill the month that is starting. This setting says what happens to the days a
                service or package is used before its first full month. A per-service "Never prorate" setting
                (in the service editor) always wins over these: that service owes its full monthly price for a
                partial first month and never earns a credit.
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="text-sm font-medium">The first partial month of a new service or package</h4>
              <SegmentedSelect
                layout="joined"
                ariaLabel="First partial month"
                options={[
                  { value: 'immediate', label: 'Invoice now' },
                  { value: 'next-invoice', label: 'On the next invoice' },
                  { value: 'none', label: 'Free (no proration)' },
                ]}
                value={form.prorationFirstMonth}
                onChange={(v) => update({ prorationFirstMonth: v })}
              />
              <p className="text-sm text-[var(--color-muted)]">
                {form.prorationFirstMonth === 'immediate'
                  ? 'An invoice for the used days (prorated by calendar day) is created and issued the moment the service starts. Days a service was globally disabled, and (when enabled below) non-payment suspended days, are credited on the next monthly invoice.'
                  : form.prorationFirstMonth === 'next-invoice'
                    ? 'The used days ride the next monthly invoice as their own prorated line, beside the new month. Days a service was globally disabled, and (when enabled below) non-payment suspended days, are credited on the next monthly invoice.'
                    : 'No proration at all: the partial first month is free, and no credit is raised for globally disabled or non-payment suspended days. Never-prorate services still owe their full first month, on the next monthly invoice.'}
              </p>
            </div>
            {form.prorationFirstMonth !== 'none' ? (
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h4 className="text-sm font-medium">Prorate retainers</h4>
                  <p className="text-sm text-[var(--color-muted)]">
                    When off, retainer services always bill their full month and earn no credit. Useful for flat
                    monthly retainers.
                  </p>
                </div>
                <ActiveToggle
                  active={form.prorationRetainers}
                  busy={false}
                  label="retainer proration"
                  onToggle={() => update({ prorationRetainers: !form.prorationRetainers })}
                />
              </div>
            ) : null}
            {/* 04 #160 WS-E (02i §8 DP-13): the lapse dialog's brand default. The dialog itself is the credit
                update (WS-C M2); the default is saved now so it is there when the question first appears. */}
            {form.prorationFirstMonth !== 'none' ? (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">When a credit has nothing left to land on</h4>
                <SegmentedSelect
                  layout="joined"
                  ariaLabel="Unabsorbed credit default"
                  options={[
                    { value: 'credit-note', label: 'Raise a credit note' },
                    { value: 'lapse', label: 'Let it lapse' },
                  ]}
                  value={form.prorationUnabsorbedCredit}
                  onChange={(v) => update({ prorationUnabsorbedCredit: v })}
                />
                <p className="text-sm text-[var(--color-muted)]">
                  When a client's last service is cancelled, a package removal leaves none, a client is archived, or a
                  monthly compose finds a credit with no charge to absorb it, you are asked what to do with the credit
                  still owed (a credit note against the invoice that billed the days, a refund with its paired credit
                  note when that invoice is paid, keep carrying it, or let it lapse), with this choice pre-selected.
                  The default is never applied on its own: closing the question keeps carrying.
                </p>
              </div>
            ) : null}
          </div>
          </div>
          <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-medium">Auto-pull unbilled items when composing</h3>
              <p className="text-sm text-[var(--color-muted)]">
                Pre-ticks "also pull all unbilled items" on the Compose monthly invoice dialog, so the month's
                time, mileage, and expenses land on the same draft. You can still override it each time.
              </p>
            </div>
            <ActiveToggle
              active={form.composeAutoPull}
              busy={false}
              label="auto-pull"
              onToggle={() => update({ composeAutoPull: !form.composeAutoPull })}
            />
          </div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-medium">Pair a credit note with invoice refunds</h3>
              <p className="text-sm text-[var(--color-muted)]">
                Pre-ticks "also create a linked credit note" when you refund part of a paid invoice, so the
                revenue and tax reverse alongside the cash. It never shows on an overpayment refund, and you
                can override it each time.
              </p>
            </div>
            <ActiveToggle
              active={form.autoCreditNoteOnRefund}
              busy={false}
              label="pair credit note"
              onToggle={() => update({ autoCreditNoteOnRefund: !form.autoCreditNoteOnRefund })}
            />
          </div>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="font-medium">Charge a convenience fee</h3>
              <p className="text-sm text-[var(--color-muted)]">
                Adds the convenience fee as a second "w/convenience fee" total on new invoices (the Cash or
                Zelle total stays fee-free). Turn it off and new invoices show a single total with no fee line, on the
                invoice and its email. Applies to newly-issued invoices only; already-issued invoices keep their
                frozen totals. The rate is the brand default; a client can still override it.
              </p>
            </div>
            {/* Toggle + rate stacked on the right; the description dominates the block height, so showing
                the rate on enable never grows the row (no proration shift). */}
            <div className="flex shrink-0 flex-col items-end gap-2">
              <ActiveToggle
                active={form.convenienceFeeEnabled}
                busy={false}
                label="convenience fee"
                onToggle={() => update({ convenienceFeeEnabled: !form.convenienceFeeEnabled })}
              />
              {form.convenienceFeeEnabled ? (
                <div className="w-24">
                  <NumberInput value={form.convenienceFeeRatePct} onChange={(v) => update({ convenienceFeeRatePct: v })} suffix="%" />
                </div>
              ) : null}
            </div>
          </div>
          </div>
        </div>
      </Card>

      {/* Sales tax: where the brand collects, its own rules and rates, its provider (04 #160 WS-E; 02i T3.4 /
          T5.4 / §8 DP-9 / item 14). Every change here is a configHistory 'global' row and applies to invoices
          issued from now on; issued invoices keep what they froze. */}
      <Card>
        <div>
          <h2 className="font-display text-xl">Sales tax: where you collect, and your own rules</h2>
          <p className="text-sm text-[var(--color-muted)]">
            A brand collects sales tax only in the states it is registered in. Until a registration is saved here,
            no tax is collected anywhere and every invoice says so in a footnote (the client owes use tax to their
            state). The platform's state profiles decide what is taxable, when and at what rate; a brand may keep
            its own rules and rates over them and is responsible for those rows. Nothing here changes an invoice
            that was already issued.
          </p>
        </div>
        {taxProfiles.length === 0 ? (
          <p className="mt-4 text-sm text-[var(--color-muted)]">
            No tax profiles are loaded on the server, so there is nowhere to register and nothing to override. Check
            the deploy if this persists.
          </p>
        ) : (
          <div className="mt-4 space-y-6">
            {/* ── Registrations (02i T3.4; owner ruling s227 B2: none saved = collects nowhere) ── */}
            <div className="space-y-3">
              <div>
                <h3 className="font-medium">Where this brand collects</h3>
                <p className="text-sm text-[var(--color-muted)]">
                  One row per state your seller's permit covers, with the permit number and the day it took effect. A
                  client billed in a state with no row here is charged no tax and the invoice footnote says so. End a
                  registration by giving it an end date; the next one for the same state starts the day after.
                </p>
              </div>
              {taxDrafts.registrations.length === 0 ? (
                <p className="rounded-md border border-[var(--tint-warn-border)] bg-[var(--tint-warn-bg-soft)] px-3 py-2 text-sm text-[var(--tint-warn-text)]">
                  <span className="font-semibold">No tax registrations saved.</span> Until one is saved, no sales tax is collected
                  anywhere for this brand, and each invoice says so in a footnote. Add California for each brand before go-live.
                </p>
              ) : null}
              {taxDrafts.registrations.map((r, i) => {
                const standing = registrationStanding(r, brandToday);
                return (
                  <div key={`reg-${i}`} className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] p-3">
                    <div className="grid gap-3 md:grid-cols-4">
                      <Field label="State" required>
                        <Select value={r.profileId} onChange={(v) => updateRegistration(i, { profileId: v })} options={stateOptions} />
                      </Field>
                      <Field label="Permit number" tip="The seller's permit (registration) number, as it appears on the permit. Optional; shown nowhere a client sees.">
                        <TextInput value={r.registrationNumber} onChange={(v) => updateRegistration(i, { registrationNumber: v })} placeholder="e.g. 123-456789" />
                      </Field>
                      <Field label="From" required tip="The day the registration took effect, on the brand's calendar. A future date is fine: nothing is collected there until that day.">
                        <input type="date" value={r.effectiveFrom} onChange={(e) => updateRegistration(i, { effectiveFrom: e.target.value })} className={dateInputClass} />
                      </Field>
                      <Field label="To" tip="Leave blank while the registration is open. Give it an end date when the brand stops collecting there; invoices issued after that day carry no tax for that state.">
                        <input type="date" value={r.effectiveTo} onChange={(e) => updateRegistration(i, { effectiveTo: e.target.value })} className={dateInputClass} />
                      </Field>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] leading-4 ${standing.live ? 'bg-[var(--tint-green-bg)] text-[var(--tint-green-text)]' : 'bg-[var(--tint-warn-bg)] text-[var(--tint-warn-text)]'}`}>{standing.label}</span>
                      <PillButton tone="red" onClick={() => removeRegistration(i)}>
                        Remove
                      </PillButton>
                    </div>
                  </div>
                );
              })}
              <Button variant="tertiary" onClick={addRegistration}>
                Add registration
              </Button>
            </div>

            {/* ── The brand's rule overlay (02i T5.4) ── */}
            <div className="space-y-3 border-t border-[rgb(var(--edge-rgb)/0.05)] pt-4">
              <div>
                <h3 className="font-medium">This brand's own rules</h3>
                <p className="text-sm text-[var(--color-muted)]">
                  A row here wins over the platform rule for its state and category from the day it takes effect (an
                  undated row applies at once); the line then records it as the brand's rule. Leave this empty unless
                  your accountant has told you a category is taxed differently for this brand. A brand rule applies on
                  the invoice date to the client's billing address.
                </p>
              </div>
              {taxDrafts.rules.map((r, i) => (
                <div key={`rule-${i}`} className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] p-3">
                  <div className="grid gap-3 md:grid-cols-4">
                    <Field label="State" required>
                      <Select value={r.profileId} onChange={(v) => updateTaxRule(i, { profileId: v })} options={stateOptions} />
                    </Field>
                    <Field label="Category" required tip="What is sold. A charge class is taxed by its own setting and a package by its members, so neither takes a rule.">
                      <Select value={r.category} onChange={(v) => updateTaxRule(i, { category: v as TaxCategory })} options={RULE_TAX_CATEGORY_OPTIONS} />
                    </Field>
                    <Field label="Taxable" required>
                      <SegmentedSelect
                        layout="joined"
                        ariaLabel="Brand rule taxable"
                        options={[
                          { value: 'yes', label: 'Taxable' },
                          { value: 'no', label: 'Not taxable' },
                        ]}
                        value={r.taxable ? 'yes' : 'no'}
                        onChange={(v) => updateTaxRule(i, { taxable: v === 'yes', ...(v === 'no' ? { taxableSharePct: '', reducedRatePct: '' } : {}) })}
                      />
                    </Field>
                    {r.taxable ? (
                      <Field label="Taxable share" tip="The part of the line that enters the taxable base. Blank = the whole line. Texas taxes 80% of a data-processing charge: enter 80.">
                        <NumberInput value={r.taxableSharePct} onChange={(v) => updateTaxRule(i, { taxableSharePct: v })} suffix="%" />
                      </Field>
                    ) : (
                      <div className="hidden md:block" aria-hidden="true" />
                    )}
                    {r.taxable ? (
                      <Field label="Reduced rate" tip="A rate for this category instead of the jurisdiction's. Blank = the jurisdiction's rate. Connecticut taxes digital goods at 1%: enter 1. A line at a reduced rate gets its own Tax line on the invoice.">
                        <NumberInput value={r.reducedRatePct} onChange={(v) => updateTaxRule(i, { reducedRatePct: v })} suffix="%" />
                      </Field>
                    ) : (
                      <div className="hidden md:block" aria-hidden="true" />
                    )}
                    <Field label="From" tip="The first invoice date the rule applies to. Blank = applies at once (and to every date).">
                      <input type="date" value={r.effectiveFrom} onChange={(e) => updateTaxRule(i, { effectiveFrom: e.target.value })} className={dateInputClass} />
                    </Field>
                    <Field label="Through" tip="The last invoice date the rule applies to. Blank = open-ended.">
                      <input type="date" value={r.effectiveTo} onChange={(e) => updateTaxRule(i, { effectiveTo: e.target.value })} className={dateInputClass} />
                    </Field>
                    <Field label="Note" tip="Why the row exists (the accountant's advice, the notice it follows). Shown on the Tax review page beside the row.">
                      <TextInput value={r.note} onChange={(v) => updateTaxRule(i, { note: v })} placeholder="e.g. per CRB, 2026-11" />
                    </Field>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-[var(--color-muted)]">
                    <span>{platformRuleWords(profileOf(r.profileId), r.category)}</span>
                    <PillButton tone="red" onClick={() => removeTaxRule(i)}>
                      Remove
                    </PillButton>
                  </div>
                </div>
              ))}
              <Button variant="tertiary" onClick={addTaxRule}>
                Add rule
              </Button>
            </div>

            {/* ── The brand's rate overlay (02i T5.4) ── */}
            <div className="space-y-3 border-t border-[rgb(var(--edge-rgb)/0.05)] pt-4">
              <div>
                <h3 className="font-medium">This brand's own rates</h3>
                <p className="text-sm text-[var(--color-muted)]">
                  A dated rate for one of a state's jurisdictions, replacing the platform rate from that day. An invoice
                  taxed at a brand rate shows it as one combined figure in its tax breakdown (no state / county / district
                  split). Leave this empty unless the platform rate is wrong for this brand.
                </p>
              </div>
              {taxDrafts.rates.map((r, i) => {
                const profile = profileOf(r.profileId);
                return (
                  <div key={`rate-${i}`} className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] p-3">
                    <div className="grid gap-3 md:grid-cols-4">
                      <Field label="State" required>
                        <Select
                          value={r.profileId}
                          onChange={(v) => updateTaxRate(i, { profileId: v, jurisdictionId: profileOf(v)?.jurisdictions[0]?.id ?? '' })}
                          options={stateOptions}
                        />
                      </Field>
                      <Field label="Jurisdiction" required>
                        <Select
                          value={r.jurisdictionId}
                          onChange={(v) => updateTaxRate(i, { jurisdictionId: v })}
                          options={(profile?.jurisdictions ?? []).map((j) => [j.id, `${j.name} (${pct(j.rate)}%)`] as const)}
                        />
                      </Field>
                      <Field label="From" required tip="The first invoice date the rate applies to, on the brand's calendar. A rate change is always dated.">
                        <input type="date" value={r.effectiveFrom} onChange={(e) => updateTaxRate(i, { effectiveFrom: e.target.value })} className={dateInputClass} />
                      </Field>
                      <Field label="Rate" required>
                        <NumberInput value={r.ratePct} onChange={(v) => updateTaxRate(i, { ratePct: v })} suffix="%" />
                      </Field>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-[var(--color-muted)]">
                      <span>{platformRateWords(profile, r.jurisdictionId)}</span>
                      <PillButton tone="red" onClick={() => removeTaxRate(i)}>
                        Remove
                      </PillButton>
                    </div>
                  </div>
                );
              })}
              <Button variant="tertiary" onClick={addTaxRate}>
                Add rate
              </Button>
            </div>

            {/* ── The determination provider (02i T5.4; read-only at 'profiles', the engine options disabled) ── */}
            <div className="border-t border-[rgb(var(--edge-rgb)/0.05)] pt-4 md:w-1/2">
              <Field
                label="Tax determination"
                tip="Where the brand's taxability and rates come from. Platform profiles: the state files maintained by the operator with the accountant, plus the brand's own rows above. A commercial tax engine (Avalara, TaxJar, Stripe Tax, Anrok) can be switched on per brand in a later update; invoices already issued never change either way."
              >
                <select value="profiles" disabled className={`${dateInputClass} opacity-70`} aria-label="Tax determination provider">
                  <option value="profiles">Platform profiles (the state files + this brand's own rows)</option>
                  <option value="avalara" disabled>Avalara (not connected)</option>
                  <option value="taxjar" disabled>TaxJar (not connected)</option>
                  <option value="stripe-tax" disabled>Stripe Tax (not connected)</option>
                  <option value="anrok" disabled>Anrok (not connected)</option>
                </select>
                <p className="mt-1 text-xs text-[var(--color-muted)]">Read-only in this build: every brand is on the platform profiles.</p>
              </Field>
            </div>
            {taxErr ? <p className="text-xs text-[var(--tint-red-text)]">{taxErr}</p> : null}
            <p className="text-xs text-[var(--color-muted)]">
              Saved with the page's Save changes button. Every change here is recorded in your change history and applies to
              invoices issued from now on.
            </p>
          </div>
        )}
      </Card>

      {/* Late fees, early-pay discounts and non-payment (slice 8b; 04 #69 the early-pay terms) */}
      <Card>
        <div>
          <h2 className="font-display text-xl">Late fees, early-pay discounts and non-payment</h2>
          <p className="text-sm text-[var(--color-muted)]">
            An optional late fee after an invoice's due date, an optional discount for paying early, and
            how long past due before a client's services suspend. The late-fee and suspension checks run
            once a day at the daily run time below; reminders, dunning notices and late-fee invoice emails
            go out at the notice time, in each client's own zone. A client can override the late fee,
            the early-pay terms and the grace period on their record.
          </p>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {/* Late fee */}
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="font-medium">Charge a late fee</h3>
                <p className="text-sm text-[var(--color-muted)]">
                  Applies once after the due date plus the grace days below, as its own invoice. Off by
                  default. A flat amount, or a percent of the overdue balance.
                </p>
              </div>
              <ActiveToggle
                active={form.lateFeeEnabled}
                busy={false}
                label="late fee"
                onToggle={() => update({ lateFeeEnabled: !form.lateFeeEnabled })}
              />
            </div>
            {form.lateFeeEnabled ? (
              <div className="space-y-4">
                <Field label="Fee type">
                  <Select
                    value={form.lateFeeType}
                    onChange={(v) => update({ lateFeeType: v as 'flat' | 'percent' })}
                    options={LATE_FEE_TYPE_OPTIONS.map(([v, l]) => [v, l] as const)}
                  />
                </Field>
                <Field label={form.lateFeeType === 'percent' ? 'Percent of overdue balance' : 'Flat amount'}>
                  <div className="w-32">
                    <NumberInput
                      value={form.lateFeeValue}
                      onChange={(v) => update({ lateFeeValue: v })}
                      suffix={form.lateFeeType === 'percent' ? '%' : '$'}
                    />
                  </div>
                </Field>
                <Field
                  label="Grace days before the fee"
                  tip="Whole days after the due date before the late fee is charged. 0 = the day after it is due."
                >
                  <div className="w-32">
                    <NumberInput value={form.lateFeeGraceDays} onChange={(v) => update({ lateFeeGraceDays: v })} suffix="days" />
                  </div>
                </Field>
              </div>
            ) : null}
            {/* 04 #69 (spec 7.24): the early-payment discount, the reward beside the penalty. Terms are
                frozen onto each invoice at issue, so a change here never touches an issued invoice. */}
            <div className="flex items-start justify-between gap-4 border-t border-[rgb(var(--edge-rgb)/0.10)] pt-4">
              <div className="min-w-0">
                <h3 className="font-medium">Offer an early-payment discount</h3>
                <p className="text-sm text-[var(--color-muted)]">
                  A percent off the invoice's ordinary lines when it is paid within a set number of days of
                  the invoice date, by Zelle, check or money order. Off by default. Offered when a payment is
                  recorded, never applied on its own; it is delivered as a credit note against the invoice.
                </p>
              </div>
              <ActiveToggle
                active={form.earlyPayEnabled}
                busy={false}
                label="early-payment discount"
                onToggle={() => update({ earlyPayEnabled: !form.earlyPayEnabled })}
              />
            </div>
            {form.earlyPayEnabled ? (
              <div className="space-y-4">
                <Field
                  label="Percent off"
                  tip="Figured on the ordinary lines after any invoice discount; never on tax, the convenience fee or charge-class lines. Sales tax on the discounted amount reverses with it."
                >
                  <div className="w-32">
                    <NumberInput value={form.earlyPayRatePct} onChange={(v) => update({ earlyPayRatePct: v })} suffix="%" />
                  </div>
                </Field>
                <Field
                  label="Days from the invoice date"
                  tip="Whole calendar days in the brand's time zone, counted from the invoice date, last day included. 2/10 net 30 is 10 here. 0 = only a payment received on the invoice date qualifies. The deadline is set on each invoice when it is issued."
                >
                  <div className="w-32">
                    <NumberInput value={form.earlyPayDays} onChange={(v) => update({ earlyPayDays: v })} suffix="days" />
                  </div>
                </Field>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.earlyPayAllowPartial}
                    onChange={(e) => update({ earlyPayAllowPartial: e.target.checked })}
                    className="mt-0.5"
                  />
                  <span>
                    Allow a pro-rata discount on a qualifying partial payment.
                    <span className="block text-xs text-[var(--color-muted)]">
                      Off (default): the discount is earned only when the invoice is paid in full within the
                      window. On: a partial payment inside the window earns its share. Brand-wide; clients
                      cannot override this part.
                    </span>
                  </span>
                </label>
              </div>
            ) : null}
            {/* 04 #69: the early-pay default's own 1.4 opt-in (a separate server flag). */}
            {earlyPayOptInApplies ? (
              <label className="mt-4 flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={applyEarlyPayToAll}
                  onChange={(e) => setApplyEarlyPayToAll(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  Apply this early-pay change to all clients, including those with overrides.
                  <span className="block text-xs text-[var(--color-muted)]">
                    {`${earlyPayOverrideCount} client${earlyPayOverrideCount === 1 ? ' has an' : 's have an'} early-pay override. `}
                    Off (default) preserves them; on reverts them to the new default. Invoices already issued keep their terms either way.
                  </span>
                </span>
              </label>
            ) : null}
          </div>
          {/* Schedule — when the daily billing jobs run */}
          <div className="space-y-4">
            <h3 className="font-medium">Schedule</h3>
            <Field
              label="Brand time zone"
              tip="Every date this brand's records carry is taken in this zone: invoice, payment and refund dates, due-day counts, document-number years, the daily late-fee and suspension checks, and the days counted between reminders and between dunning notices. The daily checks run at the run time below, in this zone. Reminder and dunning emails arrive at the notice time below, in each client's own zone (from their address state, else this one). A change applies going forward; dates already recorded never move."
            >
              <Select
                value={form.timezone}
                onChange={(v) => update({ timezone: v })}
                options={
                  TIMEZONE_OPTIONS.some(([v]) => v === form.timezone)
                    ? TIMEZONE_OPTIONS
                    : [[form.timezone, form.timezone] as const, ...TIMEZONE_OPTIONS]
                }
              />
            </Field>
            <Field
              label="Daily run time"
              tip="Local hour, in this brand's zone, the daily late-fee and suspension checks run (default 11 PM). A suspension is notified as soon as the check makes it; a late-fee invoice is issued here but its email waits for the notice time."
            >
              <Select
                value={form.dailyRunHour}
                onChange={(v) => update({ dailyRunHour: v })}
                options={RUN_HOUR_OPTIONS.map(([v, l]) => [v, l] as const)}
              />
            </Field>
            <Field
              label="Notice time"
              tip="Local hour payment reminders, dunning emails and late-fee invoice emails go out (default 9 AM), read in each client's own zone (from their address state, else this brand's). The first dunning notice follows the suspension by the repeat interval below."
            >
              <Select
                value={form.noticeHour}
                onChange={(v) => update({ noticeHour: v })}
                options={RUN_HOUR_OPTIONS.map(([v, l]) => [v, l] as const)}
              />
            </Field>
          </div>
        </div>
        {/* Suspension policy (slice 8c) — which services suspend, and how the grace window is measured. */}
        <div className="mt-4 border-t border-[rgb(var(--edge-rgb)/0.05)] pt-4">
          <h3 className="font-medium">What gets suspended</h3>
          <p className="text-sm text-[var(--color-muted)]">
            When a client is past due, choose whether all of their services suspend together or only the
            ones on the unpaid invoice.
          </p>
          <div className="mt-3 grid gap-4 md:grid-cols-2">
            <Field
              label="Suspension scope"
              tip="All services: any past-due invoice suspends everything the client has. Only services on the unpaid invoice: a past-due recurring invoice suspends just the service it bills, so the rest stay on. One-off and late-fee invoices bill no recurring service, so on their own they suspend nothing in this mode."
            >
              <Select
                value={form.suspensionScope}
                onChange={(v) => update({ suspensionScope: v === 'invoice-linked' ? 'invoice-linked' : 'all' })}
                options={[
                  ['all', 'All services together'],
                  ['invoice-linked', 'Only services on the unpaid invoice'],
                ]}
              />
            </Field>
            {form.suspensionScope === 'invoice-linked' ? (
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h4 className="text-sm font-medium">A past-due one-off suspends everything</h4>
                  <p className="text-sm text-[var(--color-muted)]">
                    Off by default. On: a past-due one-off invoice suspends all of the client's services,
                    even though it bills no recurring service.
                  </p>
                </div>
                <ActiveToggle
                  active={form.suspendOnOneOff}
                  busy={false}
                  label="one-off suspends all"
                  onToggle={() => update({ suspendOnOneOff: !form.suspendOnOneOff })}
                />
              </div>
            ) : null}
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <Field
              label="Grace period basis"
              tip="A fixed number of days: suspend the set number of days past the due date (with the per-client override). Match the invoice's payment terms: the grace equals the invoice's own term length, so NET45 suspends 45 days after the due date."
            >
              <Select
                value={form.suspendGraceMode}
                onChange={(v) => update({ suspendGraceMode: v === 'match-terms' ? 'match-terms' : 'fixed' })}
                options={[
                  ['fixed', 'A fixed number of days'],
                  ['match-terms', "Match the invoice's payment terms"],
                ]}
              />
            </Field>
            {form.suspendGraceMode === 'match-terms' ? (
              <Field
                label="Grace for due-on-receipt invoices"
                tip="Due-on-receipt invoices have no term length, so match-terms would otherwise give them a zero-day window. This is the grace to use for those instead."
              >
                <div className="w-32">
                  <NumberInput
                    value={form.matchTermsOnReceiptGraceDays}
                    onChange={(v) => update({ matchTermsOnReceiptGraceDays: v })}
                    suffix="days"
                  />
                </div>
              </Field>
            ) : (
              <Field
                label="Suspend for non-payment after"
                tip="Days after an invoice's due date before this client's services are suspended for non-payment (spec §7.10; default 15). On payment they auto-reactivate. This is the billing suspension, NOT the data-loss warning under Service lifecycle. A client can override it on their record."
              >
                <div className="w-32">
                  <NumberInput value={form.suspendGraceDays} onChange={(v) => update({ suspendGraceDays: v })} suffix="days" />
                </div>
              </Field>
            )}
          </div>
        </div>
        {/* Charge-through vs credit for suspended time (slice 8c-remainder WS3). */}
        <div className="mt-4 border-t border-[rgb(var(--edge-rgb)/0.05)] pt-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="font-medium">Credit suspended time on reactivation</h3>
              <p className="text-sm text-[var(--color-muted)]">
                Off by default: a client suspended for non-payment is still billed for the suspended
                period (the industry norm). On: the next monthly invoice credits the days each service
                or package was suspended for non-payment, prorated the same way as a service disable.
              </p>
            </div>
            <ActiveToggle
              active={form.creditSuspendedTime}
              busy={false}
              label="credit suspended time"
              onToggle={() => update({ creditSuspendedTime: !form.creditSuspendedTime })}
            />
          </div>
        </div>
        {/* Reminder + dunning timing (slice 8b) — brand-level. Notices send at the client's local
            hour (their state's timezone, else this brand's), at the notice time above (04 #138). */}
        <div className="mt-4 border-t border-[rgb(var(--edge-rgb)/0.05)] pt-4">
          <h3 className="font-medium">Payment reminders</h3>
          <p className="text-sm text-[var(--color-muted)]">
            Reminders go out at the client's local time (their state's time zone, or this brand's time
            zone if unknown), at the notice time above. The first reminder is anchored either at the
            halfway point to the due date or a set number of days before it.
          </p>
          <div className="mt-3 grid gap-4 md:grid-cols-2">
            <Field label="First reminder">
              <Select
                value={form.reminderAnchor}
                onChange={(v) => update({ reminderAnchor: v === 'days-before-due' ? 'days-before-due' : 'midpoint' })}
                options={[
                  ['midpoint', 'Halfway to the due date'],
                  ['days-before-due', 'A set number of days before due'],
                ]}
              />
            </Field>
            {form.reminderAnchor === 'days-before-due' ? (
              <Field label="Days before due" tip="How many days before the due date the first reminder is sent.">
                <div className="w-32">
                  <NumberInput
                    value={form.reminderLeadDaysBeforeDue}
                    onChange={(v) => update({ reminderLeadDaysBeforeDue: v })}
                    suffix="days"
                  />
                </div>
              </Field>
            ) : null}
            <Field label="Then repeat every" tip="After the first reminder, how often to send another until the invoice is paid.">
              <div className="w-32">
                <NumberInput value={form.reminderIntervalDays} onChange={(v) => update({ reminderIntervalDays: v })} suffix="days" />
              </div>
            </Field>
            <Field
              label="After suspension, repeat every"
              tip="Once a client is suspended for non-payment, how often the payment-required notice is re-sent (2 days = the spec's every 48 hours)."
            >
              <div className="w-32">
                <NumberInput
                  value={form.postSuspensionIntervalDays}
                  onChange={(v) => update({ postSuspensionIntervalDays: v })}
                  suffix="days"
                />
              </div>
            </Field>
          </div>
        </div>
        {/* §1.4 opt-in — only when a changed default has overrides to clear */}
        {lifecycleOptInApplies ? (
          <label className="mt-4 flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={applyLifecycleToAll}
              onChange={(e) => setApplyLifecycleToAll(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Apply these late-fee and grace changes to all clients, including those with overrides.
              <span className="block text-xs text-[var(--color-muted)]">
                {lateFeeChanged && lateFeeOverrideCount > 0
                  ? `${lateFeeOverrideCount} client${lateFeeOverrideCount === 1 ? ' has a' : 's have'} a late-fee override. `
                  : ''}
                {graceChanged && suspendGraceOverrideCount > 0
                  ? `${suspendGraceOverrideCount} client${suspendGraceOverrideCount === 1 ? ' has a' : 's have'} a grace override. `
                  : ''}
                Off (default) preserves them; on reverts them to the new default.
              </span>
            </span>
          </label>
        ) : null}
      </Card>

      {/* Automation — scheduled billing jobs (slice 8b) */}
      {/* Charge classes + voluntary tips (04 #60 WS1; spec 7.23). Catalog edits are billing config
          (configHistory 'global'); a class is soft-disabled, never deleted; amounts and taxability
          freeze onto each invoice at issue, so edits here never change issued invoices. */}
      <Card>
        <div>
          <h2 className="font-display text-xl">Charge classes and tips</h2>
          <p className="text-sm text-[var(--color-muted)]">
            Extra charges this brand can add to invoices: an automatic gratuity or service charge, a
            program fee, shipping. Each appears as its own invoice line and books either to the
            income account you pick or, for money you collect on behalf of someone else, to a
            liability owed to that payee. Classes apply brand-wide (no per-client versions) and are
            never deleted; deactivate one to stop new use. What an issued invoice shows never changes.
          </p>
        </div>
        <div className="mt-4 space-y-3">
          {chargeClasses.every((c) => statusOf(c) === 'inactive') ? (
            <p className="text-sm text-[var(--color-muted)]">No active charge classes.</p>
          ) : null}
          {classActionHint ? <p className="text-xs text-[var(--color-muted)]">{classActionHint}</p> : null}
          {chargeClasses.map((c, i) => (statusOf(c) !== 'inactive' ? renderClassRow(c, i) : null))}
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="tertiary" onClick={addChargeClass}>
              Add charge class
            </Button>
            {classesDirty && !inRowSaveCoversAll ? (
              <Button onClick={onSave} loading={saving} disabled={saving || !!stdRateErr || !!aiRateErr || !!chargeErr || !!taxErr}>
                Save changes
              </Button>
            ) : null}
            {classesDirty && !inRowSaveCoversAll && chargeErr ? <span className="text-xs text-[var(--tint-red-text)]">{chargeErr}</span> : null}
          </div>
          {classResult ? <p className="text-sm text-[var(--tint-green-text)]">{classResult}</p> : null}
          {classError ? <p className="text-sm text-[var(--tint-red-text)]">{classError}</p> : null}
        </div>
        {chargeClasses.some((c) => statusOf(c) === 'inactive') ? (
          <div className="mt-4 border-t border-[rgb(var(--edge-rgb)/0.05)] pt-4">
            <h3 className="font-medium">Deactivated classes</h3>
            <p className="text-sm text-[var(--color-muted)]">
              Kept for history; a deactivated class can still appear on invoices issued while it was
              active. Reactivate to use it again (it asks first, because an auto-applied class
              returns to every new invoice). Edit opens its details; changes never touch issued invoices.
            </p>
            {classActionHint ? <p className="text-xs text-[var(--color-muted)]">{classActionHint}</p> : null}
            <div className="mt-2 space-y-3">
              {chargeClasses.map((c, i) => (statusOf(c) === 'inactive' ? renderClassRow(c, i) : null))}
            </div>
          </div>
        ) : null}
        <div className="mt-4 border-t border-[rgb(var(--edge-rgb)/0.05)] pt-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="font-medium">Voluntary tips</h3>
              <p className="text-sm text-[var(--color-muted)]">
                Off by default. When on, the payment dialogs offer a Tip field, and a tip may be named
                through one of the active charge classes above; named through a class collected on
                behalf of someone, the tip is owed to that payee rather than kept as income. A voluntary
                tip is recorded on the payment only: it never changes the invoice, is never taxed, and
                is reversed only by voiding the payment. For a mandatory gratuity, use a charge class above.
              </p>
            </div>
            <ActiveToggle
              active={form.gratuityEnabled}
              busy={false}
              label="voluntary tips"
              onToggle={() => update({ gratuityEnabled: !form.gratuityEnabled })}
            />
          </div>
        </div>
      </Card>

      <Card>
        <div>
          <h2 className="font-display text-xl">Automation</h2>
          <p className="text-sm text-[var(--color-muted)]">
            Turn the scheduled billing jobs on or off for this brand. When the hourly automation is off,
            nothing runs automatically; you can still do everything by hand. Each job can also be turned
            off on its own.
          </p>
        </div>
        <div className="mt-4 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="font-medium">Hourly billing automation</h3>
              <p className="text-sm text-[var(--color-muted)]">
                The master switch. Off = late fees, reminders, and non-payment suspension are never run
                automatically for this brand.
              </p>
            </div>
            <ActiveToggle
              active={form.sweepEnabled}
              busy={false}
              label="automation"
              onToggle={() => update({ sweepEnabled: !form.sweepEnabled })}
            />
          </div>
          {form.sweepEnabled ? (
            <div className="space-y-3 border-t border-[rgb(var(--edge-rgb)/0.05)] pt-4">
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm">Charge late fees</span>
                <ActiveToggle
                  active={form.lateFeesJobEnabled}
                  busy={false}
                  label="late-fee job"
                  onToggle={() => update({ lateFeesJobEnabled: !form.lateFeesJobEnabled })}
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm">Send payment reminders</span>
                <ActiveToggle
                  active={form.remindersJobEnabled}
                  busy={false}
                  label="reminder job"
                  onToggle={() => update({ remindersJobEnabled: !form.remindersJobEnabled })}
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm">Suspend for non-payment</span>
                <ActiveToggle
                  active={form.suspensionJobEnabled}
                  busy={false}
                  label="suspension job"
                  onToggle={() => update({ suspensionJobEnabled: !form.suspensionJobEnabled })}
                />
              </div>
            </div>
          ) : (
            <p className="text-xs text-[var(--color-muted)]">
              Individual job switches appear when the automation is on.
            </p>
          )}
        </div>
      </Card>

      {/* Appearance */}
      <Card>
        <div>
          <h2 className="font-display text-xl">Appearance</h2>
          <p className="text-sm text-[var(--color-muted)]">
            Default glass intensity for this brand's clients (per-client overrides come later).
          </p>
        </div>
        <div className="mt-4 md:w-1/2">
          <Field label="Glass intensity (default)">
            <Select
              value={form.glass}
              onChange={(v) => update({ glass: v as GlassIntensity })}
              options={GLASS_OPTIONS}
            />
          </Field>
        </div>
      </Card>

      {error ? <p className="text-sm text-[var(--tint-red-text)]">Error: {error}</p> : null}
      {result ? <p className="text-sm text-[var(--tint-green-text)]">{result}</p> : null}

      <div className="flex items-center gap-3">
        <Button onClick={onSave} loading={saving} disabled={saving || !!stdRateErr || !!aiRateErr || !!chargeErr || !!taxErr}>
          Save changes
        </Button>
        {stdRateErr || aiRateErr ? (
          <span className="text-xs text-[var(--tint-red-text)]">Fix the pricing rate to save.</span>
        ) : chargeErr ? (
          <span className="text-xs text-[var(--tint-red-text)]">{chargeErr}</span>
        ) : taxErr ? (
          <span className="text-xs text-[var(--tint-red-text)]">{taxErr}</span>
        ) : marginChanged ? (
          <span className="text-xs text-[var(--color-muted)]">
            Pricing change will reprice this brand's auto-priced services.
          </span>
        ) : null}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Apply changes to all clients?"
        confirmLabel="Apply to all"
        busy={saving}
        onConfirm={() => void doSave()}
        onCancel={() => setConfirmOpen(false)}
      >
        <p>These revert per-client overrides to the new defaults and can't be undone in bulk:</p>
        <ul className="mt-2 list-disc pl-5 text-sm">
          {marginChanged && applyToAll && overrideCount > 0 ? (
            <li>
              <strong>{overrideCount}</strong> price override{overrideCount === 1 ? '' : 's'} to the new
              margin-derived price.
            </li>
          ) : null}
          {applyLifecycleToAll && lateFeeChanged && lateFeeOverrideCount > 0 ? (
            <li>
              <strong>{lateFeeOverrideCount}</strong> late-fee override{lateFeeOverrideCount === 1 ? '' : 's'} to the
              new late-fee default.
            </li>
          ) : null}
          {applyLifecycleToAll && graceChanged && suspendGraceOverrideCount > 0 ? (
            <li>
              <strong>{suspendGraceOverrideCount}</strong> grace-period override
              {suspendGraceOverrideCount === 1 ? '' : 's'} to the new grace default.
            </li>
          ) : null}
          {applyEarlyPayToAll && earlyPayChanged && earlyPayOverrideCount > 0 ? (
            <li>
              <strong>{earlyPayOverrideCount}</strong> early-pay override{earlyPayOverrideCount === 1 ? '' : 's'} to the
              new early-pay default.
            </li>
          ) : null}
        </ul>
      </ConfirmDialog>

      {/* 04 #60 WS2b: the deactivation flow. All three dialogs act immediately (never staged). */}
      <ConfirmDialog
        open={deactIdx !== null}
        title={deactIdx !== null ? `Deactivate ${chargeClasses[deactIdx]?.name.trim() || 'this class'}?` : 'Deactivate?'}
        confirmLabel={deactDate && deactDate === brandLocalDate(brandTimezone) ? 'Deactivate now' : 'Schedule'}
        busy={classBusy}
        onConfirm={() => { if (deactDate && deactDate >= brandLocalDate(brandTimezone) && bulkChoiceReady) void confirmDeactivate(); }}
        onCancel={() => setDeactIdx(null)}
      >
        {deactIdx !== null ? (() => {
          const c = chargeClasses[deactIdx];
          const refs = draftsFor(c?.id);
          const today = brandLocalDate(brandTimezone);
          const immediate = deactDate === today;
          const pastDate = !!deactDate && deactDate < today;
          return (
            <div className="text-sm">
              <p>
                New invoices stop getting this class from the date below. Invoices already issued never change,
                and a draft issued before that date is charged as usual.
              </p>
              <label className="mt-3 block">
                <span className="text-xs text-[var(--color-muted)]">Deactivation date (today = right now; dates are in the brand's timezone)</span>
                <input
                  type="date"
                  value={deactDate}
                  min={today}
                  onChange={(e) => setDeactDate(e.target.value)}
                  className="mt-1 w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                />
              </label>
              {pastDate ? <p className="mt-1 text-xs text-[var(--tint-red-text)]">The date cannot be in the past.</p> : null}
              {!deactDate ? <p className="mt-1 text-xs text-[var(--tint-red-text)]">Pick a date.</p> : null}
              {renderDraftCount(refs, refs.length === 1 ? 'references this class today' : 'reference this class today')}
              {refs.length > 0 && immediate && c?.id ? renderBulkChoice(c.id) : null}
              {refs.length > 0 && !immediate ? (
                <p className="mt-2 text-xs text-[var(--color-muted)]">
                  Nothing changes on those drafts before the date; a draft issued before then is charged as usual. From that
                  date the class's card offers one choice for the drafts that still reference it.
                </p>
              ) : null}
              {bulkChoice === 'replace' && !bulkReplacement && immediate && refs.length > 0 ? (
                <p className="mt-1 text-xs text-[var(--tint-red-text)]">Choose the replacement class.</p>
              ) : null}
            </div>
          );
        })() : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={reactivateIdx !== null}
        title={reactivateIdx !== null ? `Reactivate ${chargeClasses[reactivateIdx]?.name.trim() || 'this class'}?` : 'Reactivate?'}
        confirmLabel="Reactivate"
        busy={classBusy}
        onConfirm={() => void confirmReactivate()}
        onCancel={() => setReactivateIdx(null)}
      >
        {reactivateIdx !== null ? (() => {
          const c = chargeClasses[reactivateIdx];
          return (
            <div className="text-sm">
              <p>
                The class becomes available on new invoices again
                {c?.autoApply ? ' and, because it is set to add automatically, it returns to every future invoice for this brand, including the monthly compose' : ''}.
                Any scheduled deactivation date is cleared.
              </p>
              {draftsFor(c?.id).length > 0 ? (
                <p className="mt-2 text-xs text-[var(--color-muted)]">Drafts that still carry it issue with it as usual; nothing on them changes.</p>
              ) : null}
            </div>
          );
        })() : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={bulkIdx !== null}
        title={bulkIdx !== null ? `Drafts still referencing ${chargeClasses[bulkIdx]?.name.trim() || 'this class'}` : 'Drafts'}
        confirmLabel="Apply"
        busy={classBusy}
        onConfirm={() => { if (bulkChoiceReady) void confirmBulk(); }}
        onCancel={() => setBulkIdx(null)}
      >
        {bulkIdx !== null ? (() => {
          const c = chargeClasses[bulkIdx];
          const refs = draftsFor(c?.id);
          return (
            <div className="text-sm">
              <p>Choose one action for every draft that still carries this class.</p>
              {renderDraftCount(refs, refs.length === 1 ? 'still references it' : 'still reference it')}
              {c?.id ? renderBulkChoice(c.id) : null}
              {bulkChoice === 'replace' && !bulkReplacement ? <p className="mt-1 text-xs text-[var(--tint-red-text)]">Choose the replacement class.</p> : null}
            </div>
          );
        })() : null}
      </ConfirmDialog>
    </div>
  );
}

// ── local field helpers (match the Services editor idiom) ──────────────────────
function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}): JSX.Element {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
    />
  );
}
function NumberInput({
  value,
  onChange,
  suffix,
  invalid,
}: {
  value: string;
  onChange: (v: string) => void;
  suffix?: string;
  invalid?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        type="number"
        step="any"
        className={['w-full rounded-md border px-3 py-2 text-sm bg-[var(--surface-solid)]', invalid ? 'border-[var(--tint-red-border)] ring-1 ring-red-500' : 'border-[rgb(var(--edge-rgb)/0.10)]'].join(' ')}
      />
      {suffix ? <span className="text-sm text-[var(--color-muted)]">{suffix}</span> : null}
    </div>
  );
}
function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<readonly [string, string]>;
}): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
    >
      {options.map(([v, label]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );
}
